import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useGeri, useDuzenlemeBitti } from '@/lib/geri';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import MerkezSecici from '@/components/MerkezSecici';
import FaturaBelgesi from '@/components/FaturaBelgesi';
import { useProducts } from '@/contexts/ProductsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Save, Upload, Sparkles, Check, X, Plus, ExternalLink, ChevronDown, Search, RefreshCw, Package, Trash2, AlertTriangle, Inbox, Link2} from 'lucide-react';
import { toast } from 'sonner';
import { birimSecenekleri } from '@/lib/birimler';
import { trNorm } from '@/lib/arama';
import { satirTutari, ambalajiNormallestir, ilkDepoyuYay, faturaToplamlari } from '@/lib/faturaKalemi';

const currencies = ['TRY', 'USD', 'EUR', 'GBP'];
const invoiceTypes = [
  { value: 'invoiced', label: 'Faturali' },
  { value: 'no_invoice', label: 'Faturasiz' },
  { value: 'pending_invoice', label: 'Fatura Bekleniyor' }
];
const confColors = { high: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', medium: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', low: 'bg-[#7a3535]/10 text-[#7a3535]' };

// ── Genel arama destekli dropdown (yeni kayıt yok) ──
function SearchableCombobox({ value, onChange, options, placeholder = 'Seçin...', searchPlaceholder = 'Ara...', clearLabel = 'Eşleşmesiz bırak', groupBy = null, 'data-testid': testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { if (open) { setQuery(''); setTimeout(() => inputRef.current?.focus(), 0); } }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = trNorm(query);
    return options.filter(o => trNorm(o.label).includes(q) || trNorm(o.code).includes(q) || trNorm(o.category).includes(q));
  }, [options, query]);

  // Gruplama
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const map = {};
    filtered.forEach(o => {
      const g = o[groupBy] || 'Diğer';
      if (!map[g]) map[g] = [];
      map[g].push(o);
    });
    return map;
  }, [filtered, groupBy]);

  const selected = options.find(o => o.value === value);
  const handleSelect = (v) => { onChange(v); setOpen(false); setQuery(''); };
  const handleClear = (e) => { e.stopPropagation(); onChange(''); };

  const renderOptions = (list) => list.map(o => (
    <button key={o.value} type="button" onClick={() => handleSelect(o.value)}
      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-[#f5f0e8] transition-colors ${o.value === value ? 'bg-[#e8dfd0] font-medium' : ''}`}>
      {o.value === value ? <Check size={12} className="text-[#3d6e4e] shrink-0" /> : <span className="w-[20px] shrink-0" />}
      <span>{o.code ? `${o.code} - ` : ''}{o.label}</span>
    </button>
  ));

  // `flex-1 min-w-0` ŞART.
  // - min-w-0 olmazsa uzun ürün adı kutuyu şişirip yandaki sütunun üzerine taşar.
  // - w-full YANLIŞTI: kutu, yanındaki "yeni ürün" düğmesini hesaba katmadan
  //   satırın tamamını kaplıyor, düğme hücrenin dışına taşıyordu.
  // Blok bağlamda (gider/demirbaş) flex-1 yok sayılır, genişlik zaten %100 olur.
  return (
    <div className="relative flex-1 min-w-0" ref={containerRef}>
      {/* Yükseklik masaüstünde satırdaki diğer kontrollerle AYNI (h-8),
          mobilde 44px dokunma hedefi. Eskiden h-10'du ve
          eşleşme kutusu tek başına daha uzun durup satırı hizasız gösteriyordu. */}
      <button type="button" onClick={() => setOpen(v => !v)} data-testid={testId}
        className="w-full h-11 md:h-8 px-2 md:px-1.5 pr-7 rounded-md border border-[#d0c4b0] bg-white text-base md:text-[13px] text-left flex items-center transition-colors hover:border-[#8a7e70] focus:outline-none focus:ring-2 focus:ring-[#3d6e4e]/20">
        <span className={selected ? 'text-[#1a1916] flex-1 truncate' : 'text-[#8a7e70] flex-1 truncate'}>
          {selected ? `${selected.code ? selected.code + ' - ' : ''}${selected.label}` : placeholder}
        </span>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {value && <span onClick={handleClear} className="text-[#8a7e70] hover:text-[#7a3535] p-0.5 rounded cursor-pointer"><X size={12} /></span>}
          <ChevronDown size={14} className={`text-[#8a7e70] transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 min-w-[320px] w-full bg-white border border-[#d0c4b0] rounded-md shadow-lg">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#d0c4b0]">
            <Search size={13} className="text-[#8a7e70] shrink-0" />
            <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex-1 text-sm bg-transparent outline-none placeholder-[#8a7e70]"
              onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }} />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            <button type="button" onClick={() => handleSelect('')}
              className="w-full text-left px-3 py-2 text-sm text-[#8a7e70] hover:bg-[#f5f0e8] transition-colors">{clearLabel}</button>
            {filtered.length === 0
              ? <p className="px-3 py-2 text-sm text-[#8a7e70]">Sonuç bulunamadı</p>
              : grouped
                ? Object.entries(grouped).map(([grp, list]) => (
                    <div key={grp}>
                      <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#8a7e70] bg-[#f5f0e8]">{grp}</p>
                      {renderOptions(list)}
                    </div>
                  ))
                : renderOptions(filtered)
            }
          </div>
        </div>
      )}
    </div>
  );
}

// Geriye dönük uyumluluk için alias
const SupplierCombobox = ({ value, onChange, suppliers, placeholder }) => (
  <SearchableCombobox
    value={value} onChange={onChange} placeholder={placeholder || 'Tedarikçi seçin (opsiyonel)'}
    searchPlaceholder="Tedarikçi ara..." clearLabel="Eşleşmesiz bırak"
    options={suppliers.map(s => ({ value: s.id, label: s.name, code: s.code }))}
    data-testid="invoice-supplier"
  />
);

export default function InvoiceNew() {
  const { products, refreshProducts } = useProducts();
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/invoices');
  const duzenlemeBitti = useDuzenlemeBitti();
  const [searchParams] = useSearchParams();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const fileRef = useRef(null);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [expenseItems, setExpenseItems] = useState([]);
  const [fixedAssetTypes, setFixedAssetTypes] = useState([]);
  const [unitMap, setUnitMap] = useState({}); // id → {name, abbreviation}
  const [units, setUnits] = useState([]); // kayıtlı tüm birimler (Ambalaj Birimi dropdown'u için)
  // {urun_id: referans fiyat} — BU FATURA HARIÇ, ürünün diğer alışlarının
  // medyanı. Kart fiyatı bu iş için uygun değil: çoğu zaman düzeltilmekte
  // olan faturanın kendisi yazmış oluyor, uyarı o zaman hiç sönmüyor.
  const [referansFiyat, setReferansFiyat] = useState({});
  const [parsing, setParsing] = useState(false);
  const [loading, setLoading] = useState(false);
  // Dosya kutusundan secilen belge. Fatura KAYDEDILDIGINDE islenmise tasinir;
  // yarida birakilirsa kutuda kalir (fatura taslaklarindaki tuzaga dusmeyelim).
  const [kutuBelgesi, setKutuBelgesi] = useState(null);
  const [kutuAcik, setKutuAcik] = useState(false);
  const [kutuBelgeleri, setKutuBelgeleri] = useState([]);
  const [kdvOnerisi, setKdvOnerisi] = useState(null);
  const [scores, setScores] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  // Fatura silme (iki aşamalı onay)
  const [showDelete, setShowDelete] = useState(false);
  // KDV oranı karttakinden farklı seçilince açılan soru: {i, yeni, kartOrani, urun, kod}
  const [kdvSoru, setKdvSoru] = useState(null);
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const { hasPermission } = useAuth();
  const silmeYetkisi = hasPermission('invoices', 'delete');
  const [deleteStep, setDeleteStep] = useState(1);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Persist draftId in URL so page refresh doesn't create a new draft
  const urlDraftId = searchParams.get('draft_id');
  const [draftId, _setDraftId] = useState(urlDraftId || null);
  const setDraftId = useCallback((id) => {
    _setDraftId(id);
    if (id && !searchParams.get('draft_id')) {
      const next = new URLSearchParams(searchParams);
      next.set('draft_id', id);
      navigate({ search: '?' + next.toString() }, { replace: true });
    }
  }, [searchParams, navigate]);
  const [form, setForm] = useState({
    supplier_id: '', invoice_number: '', invoice_date: '', due_days: '', due_date: '',
    currency: 'TRY', exchange_rate: '1', notes: '',
    parsed_supplier_name: '', parsed_supplier_tax: '', warehouse_id: '',
    invoice_type: 'invoiced', kdv_inclusive: false,
    // Kârlılık merkez bazında ölçülüyor; fatura bir işlem kaydı, merkez taşır.
    cost_center_id: ''
  });
  const [items, setItems] = useState([]);
  // Mal kabul bağlama: cariye ait AÇIK teslimat kalemleri
  const [acikKalemler, setAcikKalemler] = useState([]);
  const [baglamaAcik, setBaglamaAcik] = useState(false);

  useEffect(() => {
    api.get('/suppliers').then(r => {
      const unique = []; const seen = new Set();
      r.data.forEach(s => { if (!seen.has(s.id)) { seen.add(s.id); unique.push(s); } });
      setSuppliers(unique);
      // URL'den gelen supplier_id varsa otomatik seç
      const preSupplier = searchParams.get('supplier');
      if (preSupplier) setForm(p => ({ ...p, supplier_id: preSupplier }));
    }).catch(() => {});
    api.get('/warehouses').then(r => setWarehouses(r.data)).catch(() => {});
    api.get('/expense-items').then(r => setExpenseItems(r.data)).catch(() => {});
    // Hata sessizce yutulmamalı: liste boş kalırsa demirbaş dropdown'u sebepsiz
    // boş görünür ve kullanıcı bunu "tür tanımlı değil" sanır.
    api.get('/fixed-asset-types').then(r => setFixedAssetTypes(r.data || [])).catch(() => {
      setFixedAssetTypes([]);
      toast.error('Demirbaş türleri yüklenemedi');
    });
    api.get('/admin/units').then(r => {
      const m = {};
      (r.data || []).forEach(u => { m[u.id] = { name: u.name, abbreviation: u.abbreviation }; });
      setUnitMap(m);
      setUnits(r.data || []);
    }).catch(() => {});
  }, []);

  // Edit mode: mevcut fatura verilerini yükle
  useEffect(() => {
    if (!isEdit) return;
    api.get(`/invoices/${editId}`).then(r => {
      setReferansFiyat(r.data.referans_fiyatlar || {});
      const inv = r.data;
      setForm({
        supplier_id: inv.supplier_id || '',
        invoice_number: inv.invoice_number || '',
        invoice_date: inv.invoice_date || '',
        due_days: inv.due_days ? String(inv.due_days) : '',
        due_date: inv.due_date || '',
        currency: inv.currency || 'TRY',
        exchange_rate: inv.exchange_rate ? String(inv.exchange_rate) : '1',
        notes: inv.notes || '',
        parsed_supplier_name: inv.parsed_supplier_name || '',
        parsed_supplier_tax: inv.parsed_supplier_tax || '',
        warehouse_id: inv.warehouse_id || '',
        invoice_type: inv.invoice_type || 'invoiced',
        cost_center_id: inv.cost_center_id || '',
        // KDV dahil/hariç seçimi faturadan okunmalı.
        //
        // DİKKAT: Burada `!!inv.kdv_inclusive` yazıyordu; kayıtta seçim YOKSA
        // (11.08.2026 öncesi faturaların tamamı böyle) ekran "Hariç"i seçili
        // gösteriyordu. Yani "işaretlenmemiş" ile "hariç işaretlenmiş" aynı
        // görünüyordu — kullanıcı faturayı açıp işaretli sanıyordu.
        // Seçim yoksa `undefined` kalır ve ekranda hiçbir seçenek yanmaz.
        kdv_inclusive: inv.kdv_inclusive === undefined || inv.kdv_inclusive === null
          ? undefined : !!inv.kdv_inclusive,
      });
      setItems((inv.items || []).map((it, i) => {
        const p = it.matched_product_id ? products.find(x => x.id === it.matched_product_id) : null;
        return {
          ...it, _idx: i,
          // Kalemin KENDİ oranı yüklenir — kart oranı eski faturayı ezmemeli.
          // Bilnex'ten aktarılan 1.044 kalemde oran `vat_rate` adıyla duruyor;
          // hiçbiri yoksa boş kalır ve kartın oranı varsayılan olur.
          kdv_rate: it.kdv_rate != null ? String(it.kdv_rate)
            : (it.vat_rate != null ? String(it.vat_rate) : ''),
          discount_rate: it.discount_rate != null ? String(it.discount_rate) : '0',
          quantity: it.quantity != null ? String(it.quantity) : '',
          unit_price: it.unit_price != null ? String(it.unit_price) : '',
          total_price: it.total_price != null ? String(it.total_price) : '0',
          package_qty: it.package_qty != null ? String(it.package_qty) : '',
          package_unit: (p?.unit_id && unitMap[p.unit_id]) ? unitMap[p.unit_id].abbreviation : (it.package_unit || p?.unit || ''),
          matched_product_id: it.matched_product_id || '',
          matched_product_name: it.matched_product_name || '',
          // Eski faturalarda depo fatura seviyesindeydi — kalemlere dağıt (geriye dönük uyum)
          warehouse_id: it.warehouse_id || inv.warehouse_id || '',
          _type: it._type || (it.matched_expense_id ? 'expense' : it.matched_fixed_asset_type_id ? 'fixed_asset' : 'product'),
          _product_unit_id: p?.unit_id || null,
          _product_unit_conversions: p?.unit_conversions || [],
        };
      }));
      if (inv.image_data) setImagePreview(inv.image_data);
    }).catch(() => {
      toast.error('Fatura yüklenemedi');
      navigate('/erp/invoices');
    });
  }, [editId, isEdit]);

  // Auto-calc due_date from due_days
  useEffect(() => {
    if (form.due_days && form.invoice_date) {
      const d = new Date(form.invoice_date);
      d.setDate(d.getDate() + parseInt(form.due_days));
      setForm(p => ({ ...p, due_date: d.toISOString().split('T')[0] }));
    }
  }, [form.due_days, form.invoice_date]);

  // BroadcastChannel - listen for new supplier creation
  useEffect(() => {
    const channel = new BroadcastChannel('erp_supplier_created');
    channel.onmessage = (event) => {
      const { supplier_id, supplier_name, supplier_code } = event.data;
      setSuppliers(prev => [...prev, { id: supplier_id, name: supplier_name, code: supplier_code }]);
      setForm(prev => ({ ...prev, supplier_id: supplier_id }));
      toast.success(`Yeni tedarikci eklendi ve eslesti: ${supplier_name}`);
    };
    return () => channel.close();
  }, []);

  const saveDraft = useCallback(async (itemIdx) => {
    try {
      const id = draftId || searchParams.get('draft_id') || `draft-${Date.now()}`;
      // Taslakta da ayni kural (bkz. etkinKdvOrani)
      const oran = (it) => {
        if (it?.kdv_rate !== '' && it?.kdv_rate != null) return String(it.kdv_rate);
        const kart = it?.matched_product_id
          ? products.find(x => x.id === it.matched_product_id) : null;
        if (kart && kart.kdv_rate != null && kart.kdv_rate !== '') return String(kart.kdv_rate);
        return '0';
      };
      // Toplamlar kuruşlu, asıl kayıtla aynı kuraldan (lib/faturaKalemi)
      const taslakToplam = faturaToplamlari(items, oran, form.kdv_inclusive);
      const res = await api.post('/invoices', {
        id,
        ...form,
        status: 'draft',
        amount_foreign: taslakToplam.genel,
        kdv_amount: taslakToplam.kdv,
        exchange_rate: parseFloat(form.exchange_rate) || 1,
        items: items.map(it => ({
          description: it.description, quantity: parseFloat(it.quantity) || 0,
          unit: it.unit, unit_price: parseFloat(it.unit_price) || 0,
          discount_rate: parseFloat(it.discount_rate) || 0,
          total_price: parseFloat(it.total_price) || 0,
          kdv_rate: parseFloat(oran(it)) || 0,
          matched_product_id: it.matched_product_id || null,
          matched_product_name: it.matched_product_name || null,
        })),
        confidence_scores: scores
      });
      setDraftId(res.data.id);
      return res.data.id;
    } catch { return draftId; }
  }, [form, items, scores, draftId, products]);

  const openNewProductForItem = useCallback((itemIdx) => {
    const item = items[itemIdx];
    const desc = encodeURIComponent(item.description || '');
    const id = draftId || searchParams.get('draft_id') || `draft-${Date.now()}`;
    window.open(`/erp/products/new?return=invoice&invoice_draft_id=${id}&prefill_name=${desc}`, '_blank');
    // Fatura sunucuya TASLAK olarak yazilir: yeni sekmeye gecerken girilenler
    // kaybolmasin diye. Sessiz olmamali — kullanici kaydettigini bilmeli,
    // vazgecerse Faturalar > Taslaklar'dan silebilmeli.
    saveDraft(itemIdx)
      .then(() => toast.info('Fatura taslak olarak saklandı', {
        description: 'Kaydetmezseniz Faturalar > Taslaklar altında kalır.' }))
      .catch(() => {});
  }, [items, draftId, searchParams, saveDraft]);

  const openNewSupplier = useCallback(() => {
    const name = encodeURIComponent(form.parsed_supplier_name || '');
    const tax = encodeURIComponent(form.parsed_supplier_tax || '');
    const id = draftId || searchParams.get('draft_id') || `draft-${Date.now()}`;
    window.open(`/erp/suppliers/new?return=invoice&invoice_draft_id=${id}&prefill_name=${name}&prefill_tax=${tax}`, '_blank');
    saveDraft()
      .then(() => toast.info('Fatura taslak olarak saklandı', {
        description: 'Kaydetmezseniz Faturalar > Taslaklar altında kalır.' }))
      .catch(() => {});
  }, [form, draftId, searchParams, saveDraft]);

  // Kutuyu ac: yalnizca islenmeyi bekleyen belgeler
  const kutuyuAc = async () => {
    try {
      const r = await api.get('/dosya-kutusu', { params: { durum: 'bekliyor,duzeltme' } });
      setKutuBelgeleri(r.data || []);
      setKutuAcik(true);
    } catch {
      toast.error('Dosya kutusu açılamadı');
    }
  };

  // Belgenin ilk sayfasini onizleme icin data URL'e cevirir.
  // Yarim kalan is geri yuklenirken AI'ya gitmedigimiz icin gorseli ayrica
  // almamiz gerekiyor; GridFS'te zaten duruyor.
  const kutuGorseliniAl = async (belgeId) => {
    try {
      const r = await api.get(`/dosya-kutusu/${belgeId}/sayfa/1`, { responseType: 'blob' });
      const veri = await new Promise((coz, red) => {
        const fr = new FileReader();
        fr.onload = () => coz(fr.result);
        fr.onerror = red;
        fr.readAsDataURL(r.data);
      });
      if (typeof veri === 'string' && veri.startsWith('data:image')) setImagePreview(veri);
    } catch { /* onizleme sart degil, giris devam etsin */ }
  };

  // Kutudaki belgeyi ac. Belge KILITLENIR — iki kisi ayni faturayi girmesin.
  //
  // YARIM KALAN IS VARSA YENIDEN OKUTMAYIZ. Eskiden tek yol AI'ya tekrar
  // okutmakti; bu hem girilmis her seyi siliyor hem de bosuna okuma yapiyordu.
  // Ustelik ayni belgenin iki okumasi ayni sonucu vermiyor (mukerrer fatura
  // incelemesinde olctuk: kalem adlari tutmadi). Kullanici isterse
  // "Yeniden Oku" ile bilerek bastan okutabilir.
  const kutudanSec = async (belge, zorlaOku = false) => {
    setKutuAcik(false);
    setParsing(true);
    try {
      await api.post(`/dosya-kutusu/${belge.id}/kilitle`);
      const kayitli = !zorlaOku ? belge.calisma?.veri : null;
      if (kayitli) {
        setKutuBelgesi(belge);
        calismayiGeriYukle(kayitli);
        await kutuGorseliniAl(belge.id);
        toast.success('Yarım kalan giriş geri yüklendi');
      } else {
        // Sayfalari sunucu GridFS'ten alir ve HEPSINI modele verir; tarayici
        // indirip yeniden yuklemez. Cok sayfali faturada kalemler tum
        // sayfalardan toplanir.
        const res = await api.post(`/invoices/parse-belge/${belge.id}`);
        setKutuBelgesi(belge);
        okumaSonucunuIsle(res.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Belge okunamadı');
      setKutuBelgesi(null);
    } finally {
      setParsing(false);
    }
  };

  // Saklanan isi ekrana geri koyar. Gorsel disarida tutuluyor (GridFS).
  const calismayiGeriYukle = (veri) => {
    if (veri.form) setForm(p => ({ ...p, ...veri.form }));
    if (Array.isArray(veri.items)) setItems(veri.items);
    if (veri.scores !== undefined) setScores(veri.scores);
    if (veri.kdvOnerisi !== undefined) setKdvOnerisi(veri.kdvOnerisi);
  };

  // Kullanici bilerek bastan okutmak isterse. Yikici bir islem (girilen her
  // sey gider), o yuzden iki adimli: ikinci tikla calisir.
  const [okumaOnayi, setOkumaOnayi] = useState(false);
  const yenidenOku = async () => {
    if (!kutuBelgesi) return;
    if (!okumaOnayi) {
      setOkumaOnayi(true);
      setTimeout(() => setOkumaOnayi(false), 5000);
      return;
    }
    setOkumaOnayi(false);
    sonSaklanan.current = '';   // yeni okuma da saklansin
    await kutudanSec(kutuBelgesi, true);
  };

  // Islemekten vazgec: belge kutuya geri doner
  const kutuyaGeriBirak = async () => {
    if (!kutuBelgesi) return;
    try {
      await api.post(`/dosya-kutusu/${kutuBelgesi.id}/birak`);
      toast.info('Belge kutuya geri bırakıldı');
    } catch { /* yoksay */ }
    setKutuBelgesi(null);
  };

  // OTOMATIK SAKLAMA — kutudan gelen bir giriste yazilan her sey belgenin
  // uzerine yazilir. Boylece sayfadan cikmak isi silmiyor.
  //
  // Fatura TASLAGI olarak yazilmiyor: taslak mekanizmasi yalnizca "yeni
  // urun/tedarikci olustur" sapmasi icin var ve KAYIPLI — depo, ambalaj,
  // kalem turu ve gider eslesmelerini tasimiyor. Buradaki anlik goruntu tam.
  //
  // Gorsel gonderilmez; GridFS'te duruyor (bkz. kutuGorseliniAl).
  const saklamaZamani = useRef(null);
  const sonSaklanan = useRef('');
  const [saklandi, setSaklandi] = useState(null);
  useEffect(() => {
    if (!kutuBelgesi || parsing || loading) return;
    const veri = { form, items, scores, kdvOnerisi };
    const imza = JSON.stringify(veri);
    if (imza === sonSaklanan.current) return;
    clearTimeout(saklamaZamani.current);
    saklamaZamani.current = setTimeout(async () => {
      try {
        const r = await api.put(`/dosya-kutusu/${kutuBelgesi.id}/calisma`, { veri });
        sonSaklanan.current = imza;
        setSaklandi(r.data?.at || new Date().toISOString());
      } catch { /* saklama basarisiz olsa da giris devam etmeli */ }
    }, 1500);
    return () => clearTimeout(saklamaZamani.current);
  }, [form, items, scores, kdvOnerisi, kutuBelgesi, parsing, loading]);

  // Dosya kutusundan DOGRUDAN gelindiyse belgeyi kendiliginden ac.
  // Urun kartlari yuklenmeden okumaya baslamayiz: eslestirme onlara bagli.
  const kutuBelgeParam = searchParams.get('kutu_belge');
  const kutuOtomatikAcildi = useRef(false);
  useEffect(() => {
    if (!kutuBelgeParam || kutuOtomatikAcildi.current || !products.length) return;
    kutuOtomatikAcildi.current = true;
    (async () => {
      try {
        const r = await api.get(`/dosya-kutusu/${kutuBelgeParam}`);
        await kutudanSec(r.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Belge açılamadı');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kutuBelgeParam, products.length]);

  const handleFileParse = async (e, directFile) => {
    const file = directFile || e?.target?.files?.[0];
    if (!file) return;
    setShowUploadModal(false);
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/invoices/parse', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      okumaSonucunuIsle(res.data);
    } catch (err) {
      toast.error('Fatura okunamadi: ' + (err.response?.data?.detail || err.message));
    }
    setParsing(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Okuma sonucunu ekrana doker. Hem tek dosya hem kutu belgesi ayni yoldan
  // gecsin diye ayrildi; iki kopya birbirinden ayri duserdi.
  const okumaSonucunuIsle = (d) => {
      setForm(p => ({
        ...p,
        supplier_id: d.matched_supplier_id || '',
        parsed_supplier_name: d.supplier_name || '',
        parsed_supplier_tax: d.supplier_tax_number || '',
        invoice_number: d.invoice_number || '',
        invoice_date: d.invoice_date || '',
        due_days: d.due_date_days ? String(d.due_date_days) : '',
        currency: d.currency || 'TRY',
        // KDV dahil/hariç, tedarikçinin GEÇMİŞ faturalarından öneriliyor.
        // Bir firma ya hep dahil ya hep hariç keser (işaretli 42 tedarikçinin
        // 42'si de kendi içinde tutarlı). Öneri yoksa seçim boş kalır ve
        // kaydetme koruması kullanıcıyı işaretlemeye zorlar.
        kdv_inclusive: d.kdv_inclusive_onerisi === null || d.kdv_inclusive_onerisi === undefined
          ? p.kdv_inclusive
          : !!d.kdv_inclusive_onerisi,
      }));
      setKdvOnerisi(
        d.kdv_inclusive_onerisi === null || d.kdv_inclusive_onerisi === undefined
          ? null
          : { dahil: !!d.kdv_inclusive_onerisi, dayanak: d.kdv_onerisi_dayanak || 0 });
      setItems((d.items || []).map((it, i) => {
        const p = it.matched_product_id ? products.find(x => x.id === it.matched_product_id) : null;
        return {
          ...it, _idx: i,
          // Tür açıkça set edilmeli; kaydetme akışı bu alana bakıyor
          _type: it.matched_expense_id ? 'expense'
            : it.matched_fixed_asset_type_id ? 'fixed_asset' : 'product',
          matched_product_id: it.matched_product_id || '',
          matched_product_name: it.matched_product_name || '',
          confidence: it.confidence || (it.matched_product_id ? 'high' : 'low'),
          discount_rate: it.discount_rate != null ? String(it.discount_rate) : '0',
          package_qty: it.package_qty != null ? String(it.package_qty) : '',
          package_unit: (p?.unit_id && unitMap[p.unit_id]) ? unitMap[p.unit_id].abbreviation : (it.package_unit || p?.unit || ''),
          warehouse_id: '',
          _product_unit_id: p?.unit_id || null,
          _product_unit_conversions: p?.unit_conversions || [],
        };
      }));
      setScores(d.confidence_scores);
      if (d.image_data) setImagePreview(d.image_data);
      const eslesen = (d.items || []).filter(x => x.matched_product_id).length;
      const sayfaBilgi = d.sayfa_sayisi > 1 ? `${d.sayfa_sayisi} sayfa okundu — ` : '';
      toast.success(
        sayfaBilgi + (d.tedarikci_urun_sayisi
          ? `${eslesen}/${(d.items || []).length} kalem eşleşti `
            + `(bu firmadan alınan ${d.tedarikci_urun_sayisi} ürün üzerinden)`
          : `${eslesen}/${(d.items || []).length} kalem eşleşti`));
  };

  const updateItem = (i, field, value) => {
    // ILK KALEMIN DEPOSU DIGER SATIRLARI DOLDURUR — kural ve gerekcesi
    // `lib/faturaKalemi.ilkDepoyuYay`ta (satis faturasiyla ortak).
    const updated = (i === 0 && field === 'warehouse_id')
      ? ilkDepoyuYay(items, value, (k) => k._type === 'product')
      : [...items];
    updated[i] = { ...updated[i], [field]: value };
    // Kalem türü değişince ÖNCEKİ türün eşleşmesi temizlenmeli. Aksi halde ürün
    // olarak eşleşmiş bir kalem gidere çevrildiğinde matched_product_id kayıtta
    // kalır; ekranda gider görünür ama sistem onu hâlâ stoklu ürün sayar.
    if (field === '_type') {
      if (value !== 'product') {
        updated[i].matched_product_id = null;
        updated[i].matched_product_name = null;
        updated[i].matched_product_code = null;
        updated[i].warehouse_id = null;
      }
      if (value !== 'expense') {
        updated[i].matched_expense_id = null;
        updated[i].matched_expense_name = null;
      }
      if (value !== 'fixed_asset') {
        updated[i].matched_fixed_asset_type_id = null;
      }
    }
    if (field === 'matched_product_id') {
      const p = products.find(x => x.id === value);
      if (p) {
        updated[i].matched_product_name = p.name;
        updated[i].matched_product_code = p.code || null;
        updated[i].confidence = 'high';
        // Urun kartinin KDV orani. Onceden 'purchase_kdv_rate' okunuyordu —
        // boyle bir alan hicbir yerde yok, o yuzden otomatik doldurma hic
        // calismiyordu. Gercek alan 'kdv_rate'.
        { const v = varsayilanKdv(p); if (v != null) updated[i].kdv_rate = v; }
        // Önceki ürün için verilmiş "kartı da güncelle" onayı / elle seçim yeni ürüne geçmesin
        updated[i].kdv_karta_yaz = false;
        updated[i]._kdv_elle = false;
        // Ürün Birimi: ürünün kayıtlı ana birimi (ambalaj içeriğinin birimi)
        const primaryUnitAbbr = p.unit_id && unitMap[p.unit_id] ? unitMap[p.unit_id].abbreviation : (p.unit || 'adet');
        updated[i].package_unit = primaryUnitAbbr;
        updated[i]._product_unit_id = p.unit_id || null;
        updated[i]._product_unit_conversions = p.unit_conversions || [];
      }
    }
    // AMBALAJ İÇERİĞİ, ürün kartındaki dönüşümden gelir.
    //
    // Ambalaj birimi (it.unit) ürünün alternatif dönüşüm birimlerinden biriyse
    // içeriği elle yazmaya gerek yok: kartta "1 koli = 12 kg" zaten kayıtlı.
    // Elle yazmak hem gereksiz hem hataya açık — yanlış içerik stoğu doğrudan
    // bozuyor (bkz. aşağıdaki "eksik" uyarısı).
    //
    // Ana birim seçilirse içerik 1'dir. Dönüşümü OLMAYAN bir birim seçilirse
    // dokunulmaz; kullanıcı elle girsin, kırmızı uyarı zaten çıkıyor.
    if (field === 'unit' || field === 'matched_product_id') {
      const kart = products.find(x => x.id === updated[i].matched_product_id);
      const secilen = field === 'unit' ? value : updated[i].unit;
      if (kart && secilen) {
        const es = birimSecenekleri(kart, units)
          .find(o => trNorm(o.deger) === trNorm(secilen));
        if (es && es.carpan > 0) updated[i].package_qty = String(es.carpan);
      }
    }
    // Auto-calc total_price: quantity × unit_price × (1 - discount/100)
    // unit_price here is always package price; total_price is what appears on the invoice
    if (field === 'quantity' || field === 'unit_price' || field === 'discount_rate' || field === 'package_qty' || field === 'package_unit') {
      // Satır tutarı BRÜT ve İSKONTO ayrı ayrı yuvarlanarak bulunur; başlıktaki
      // "İskontosuz Toplam − İskonto = KDV Matrahı" ancak böyle birebir tutar.
      const qty = parseFloat(field === 'quantity' ? value : updated[i].quantity) || 0;
      const price = parseFloat(field === 'unit_price' ? value : updated[i].unit_price) || 0;
      const disc = parseFloat(field === 'discount_rate' ? value : updated[i].discount_rate) || 0;
      const brut = Number((qty * price).toFixed(2));
      const iskonto = Number((brut * disc / 100).toFixed(2));
      updated[i].total_price = (brut - iskonto).toFixed(2);
    }
    setItems(updated);
  };
  const rematchItem = (i) => {
    const desc = (items[i].description || '').toLowerCase().trim();
    if (!desc) return;
    // Muhafazakâr eşleştirme (backend `_fuzzy_match_product` ile aynı kurallar):
    // tek kelimenin tesadüfen geçmesi eşleşme sayılmaz. Eskiden "GREEN WORLD REF"
    // → "Efes Pilsen Green" gibi alakasız eşleşmeler üretiliyordu.
    // Türkçe karakterler sadeleştirilir ("GOGUS"/"göğüs" aynı sayılsın).
    // Rakamlar korunur ("200 GR" ≠ "125 GR") ama parçalı eşleşmeye girmez.
    const trNorm = (s) => (s || '')
      .replace(/[İIı]/g, 'i').replace(/[çÇ]/g, 'c').replace(/[ğĞ]/g, 'g')
      .replace(/[öÖ]/g, 'o').replace(/[şŞ]/g, 's').replace(/[üÜ]/g, 'u')
      .toLowerCase();
    const tokenize = (s) => trNorm(s).split(/[^\w]+/).filter(w => w.length > 2);
    const words = tokenize(desc);
    const scored = products.map(p => {
      const hay = tokenize(`${p.name} ${p.code || ''}`);
      const score = words.reduce((s, w) => {
        if (hay.includes(w)) return s + 1;
        if (w.length >= 5 && !/^\d+$/.test(w) && hay.some(h => h.includes(w))) return s + 0.5;
        return s;
      }, 0);
      return { p, score };
    }).filter(x => x.score > 0).sort((a, b) => b.score - a.score);
    const top = scored[0];
    const yeterli = top && words.length > 0
      && (top.score / words.length) >= 0.6
      && (words.length === 1 || top.score >= 2);
    const best = yeterli ? top.p : undefined;
    const updated = [...items];
    const primaryUnitAbbr = best?.unit_id && unitMap[best.unit_id] ? unitMap[best.unit_id].abbreviation : (best?.unit || null);
    updated[i] = {
      ...updated[i],
      matched_product_id: best?.id || '',
      matched_product_name: best?.name || '',
      matched_product_code: best?.code || null,
      confidence: best ? 'high' : 'low',
      ...(varsayilanKdv(best) != null ? { kdv_rate: varsayilanKdv(best) } : {}),
      kdv_karta_yaz: false,
      _kdv_elle: false,
      ...(best && primaryUnitAbbr ? { package_unit: primaryUnitAbbr, _product_unit_id: best.unit_id, _product_unit_conversions: best.unit_conversions || [] } : {}),
    };
    setItems(updated);
    if (best) toast.success(`Eşleştirildi: ${best.name}`);
    else toast.info('Eşleşen ürün bulunamadı');
  };

  const removeItem = (i) => setItems(items.filter((_, j) => j !== i));

  // Depo artık kalem bazında seçilir (bir faturanın kalemleri farklı depolara girebilir).
  // Yeni kalem eklenirken son kullanılan depo varsayılan gelir — kullanıcı değiştirebilir.
  const lastWarehouseId = useMemo(() => {
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].warehouse_id) return items[i].warehouse_id;
    }
    return form.warehouse_id || '';
  }, [items, form.warehouse_id]);

  // BU FİRMADAN SON ALIMDAKİ KDV ORANLARI {urun_id: {oran, tarih, fatura_no}}.
  // Aynı ürün firmaya (markaya) göre farklı oranla geliyor; firma çoğunlukla
  // hep aynı markayı sattığı için en iyi varsayılan o firmadan son alımdır,
  // kart yalnızca geçmiş yoksa devreye girer (kullanıcı, 2026-09-28).
  // Düzenlenen fatura kendi kendine öneri olmasın diye hariç tutulur.
  const [firmaKdv, setFirmaKdv] = useState({});
  useEffect(() => {
    if (!form.supplier_id) { setFirmaKdv({}); return; }
    let iptal = false;
    api.get('/invoices/tedarikci-kdv-oranlari',
      { params: { supplier_id: form.supplier_id, haric_id: editId || '' } })
      .then(r => { if (!iptal) setFirmaKdv(r.data || {}); })
      .catch(() => { if (!iptal) setFirmaKdv({}); });
    return () => { iptal = true; };
  }, [form.supplier_id, editId]);

  // Ürün seçilince gelen oran: bu firmadan son alım → kart → yok (null)
  const varsayilanKdv = (urun) => {
    if (!urun) return null;
    const f = firmaKdv[urun.id];
    if (f && f.oran != null) return String(f.oran);
    if (urun.kdv_rate != null && urun.kdv_rate !== '') return String(urun.kdv_rate);
    return null;
  };

  // Cari seçilince o cariden faturası BEKLEYEN mal kabul kalemleri getirilir.
  // Fatura bunları kapatabilir; kapatılan kalemde stok ikinci kez yazılmaz.
  useEffect(() => {
    if (!form.supplier_id) { setAcikKalemler([]); return; }
    let iptal = false;
    api.get('/teslimat/acik-kalemler', { params: { supplier_id: form.supplier_id } })
      .then(r => { if (!iptal) setAcikKalemler(r.data?.kalemler || []); })
      .catch(() => { if (!iptal) setAcikKalemler([]); });
    return () => { iptal = true; };
  }, [form.supplier_id]);

  // Seçilen mal kabul kalemini faturaya satır olarak ekler. Miktar ve fiyat
  // düzenlenebilir kalır — genelde bir irsaliye tam faturalanır ama her zaman
  // değil (kısmi kapatma).
  const teslimattanEkle = (tk) => {
    const kart = products.find(p => p.id === tk.matched_product_id);
    setItems(prev => ([...prev, {
      _type: tk._type || 'product',
      description: tk.description || tk.matched_product_name || '',
      quantity: String(tk.kalan_miktar),
      unit: tk.unit || '',
      unit_price: tk.unit_price != null ? String(tk.unit_price) : '',
      discount_rate: '0',
      package_qty: tk.package_qty != null ? String(tk.package_qty) : '',
      package_unit: kart?.unit || tk.package_unit || '',
      total_price: String((tk.kalan_miktar || 0) * (tk.unit_price || 0)),
      kdv_rate: varsayilanKdv(kart) ?? '',
      matched_product_id: tk.matched_product_id || '',
      matched_product_name: tk.matched_product_name || '',
      matched_expense_id: tk.matched_expense_id || '',
      matched_fixed_asset_type_id: tk.matched_fixed_asset_type_id || '',
      warehouse_id: tk.warehouse_id || lastWarehouseId,
      confidence: 'high',
      teslimat_ref: { belge_id: tk.belge_id, kalem_id: tk.kalem_id,
                      miktar: tk.kalan_miktar },
    }]));
    setAcikKalemler(prev => prev.filter(
      k => !(k.belge_id === tk.belge_id && k.kalem_id === tk.kalem_id)));
    toast.success(`${tk.matched_product_name || 'Kalem'} faturaya eklendi`);
  };

  // Satirin dayandigi urun karti — ana birim ve son alis fiyati oradan okunur.
  const satirKarti = (it) => products.find(p => p.id === it.matched_product_id) || null;

  // AMBALAJ İÇERİĞİ HER ZAMAN ÜRÜNÜN ANA BİRİMİNDE tutulur. Ekrana gelen eski
  // kayıtlar alt birimde olabiliyor; listeler yüklenince düzeltilir. Kural ve
  // gerekçesi `lib/faturaKalemi.ambalajiNormallestir`ta (satışla ortak).
  // `items` de bağımlılıkta olmalı: fatura verisi ürün/birim listelerinden
  // SONRA geliyorsa efekt bir daha çalışmıyor ve satırlar normallenmeden
  // kalıyordu. Değişiklik yoksa `prev` aynen döndüğü için döngü olmaz.
  useEffect(() => {
    if (!products.length || !units.length) return;
    setItems(prev => ambalajiNormallestir(prev, products, units));
  }, [items, products, units]);

  // Ambalaj icerigi ANA BIRIM cinsinden beklenir. Fatura "1000 GR" diyorsa ve
  // kullanici 1000 yazarsa, 1000 kg sayilir: stok 1000 kat siser, birim fiyat
  // 1000 kat duser. Kartta kayitli son alis fiyatiyla karsilastirip yakaliyoruz.
  const SAPMA_KATI = 10;
  // Ana birimi kg/lt olan bir üründe "1 ambalajda 200+ kg" gerçekçi değil.
  // Eşik gerçek veriyle seçildi: 1516 sağlam satırda en büyük içerik 180 kg,
  // yani bu eşik hiçbir doğru satırı yanlış işaretlemiyor.
  const BUYUK_ICERIK = 200;
  const BUYUK_BIRIMLER = ['kg', 'lt'];

  const icerikUyarisi = (it) => {
    const kart = satirKarti(it);
    const yazilan = parseFloat(it.package_qty);
    const fiyat = parseFloat(it.unit_price);
    if (!kart) return null;
    const birim = (kart.unit || '').toLowerCase();
    // İçerik boşsa sistem 1 sayar; kural oraya da uygulanmalı, yoksa
    // "10 adet Cola @ 214,99" (aslında koli fiyatı) hiç uyarı vermiyor.
    const bos = !(yazilan > 0);
    const icerik = bos ? 1 : yazilan;

    // 1) Referans fiyattan ciddi sapma.
    //    Öncelik BU FATURA HARİÇ hesaplanan referans; yoksa kart fiyatı.
    const referans = parseFloat(referansFiyat[kart.id]);
    const kayitli = referans > 0 ? referans : parseFloat(kart.last_purchase_price);
    if (fiyat > 0 && kayitli > 0) {
      const turetilen = fiyat / icerik;
      const kat = turetilen > kayitli ? turetilen / kayitli : kayitli / turetilen;
      if (kat >= SAPMA_KATI) {
        return { tur: bos ? 'bos' : 'sapma', kayitli, kat,
                 kaynak: referans > 0 ? 'gecmis' : 'kart',
                 dusuk: turetilen < kayitli, birim: kart.unit || '' };
      }
    }
    // 2) İçerik ana birime göre fazla büyük (referans gerektirmez)
    if (!bos && BUYUK_BIRIMLER.includes(birim) && icerik >= BUYUK_ICERIK) {
      return { tur: 'buyuk', icerik, birim: kart.unit || '' };
    }
    return null;
  };

  const addItem = () => setItems([...items, { _type: 'product', description: '', quantity: '', unit: 'adet', unit_price: '', discount_rate: '0', package_qty: '', package_unit: '', total_price: '0', kdv_rate: '', matched_product_id: '', warehouse_id: lastWarehouseId, confidence: 'low' }]);

  // Faturada fiilen kullanılan depolar (başlıkta özet göstermek için)
  const usedWarehouses = useMemo(() => {
    const ids = [...new Set(items.filter(it => it.matched_product_id && it.warehouse_id).map(it => it.warehouse_id))];
    return ids.map(id => warehouses.find(w => w.id === id)).filter(Boolean);
  }, [items, warehouses]);

  const fmtTR = (n) => n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ─── Kalem tablosu: ortak kontrol sınıfları ───
  //
  // Her hücrede ayrı ayrı yazılan sınıflar zamanla birbirinden ayrıştı: kimi
  // kutu 8, kimi 9 birim yüksekliğinde, kimi sola kimi sağa hizalı, odak
  // halkası kiminde var kiminde yok. Sütun genişlikleri de <th> içine
  // dağılmıştı; dar kalanlarda "%0" ve "— Stok girişi —" gibi değerler
  // kırpılıyordu. Tek kaynak:
  //   - genişlikler <colgroup> içinde,
  //   - kontrol görünümü aşağıdaki sabitlerde.
  // Mobilde hucreler blok olur (tablo yatay kaymaz), masaustunde hucre kalir.
  const HUCRE = 'block md:table-cell px-1.5 py-1 md:py-1.5 align-top';
  // Baslik formundaki alan etiketi. Sabit yukseklik: `NET` rozeti olan
  // etiketler rozetsizlerden yuksek kaliyor, altindaki kutu asagi kayiyordu.
  const ALAN_ETIKET = 'flex items-center gap-1 h-4 text-[10px] font-semibold ' +
    'text-[#8a7e70] uppercase mb-1 leading-none';
  // Yalnizca mobilde gorunen hucre etiketi — baslik satiri mobilde gizli
  const ETIKET = 'md:hidden block text-[10px] font-semibold text-[#8a7e70] uppercase mb-0.5';
  const KONTROL = 'h-11 md:h-8 rounded-md border bg-white text-base md:text-[13px] text-[#1a1916] ' +
    'focus:outline-none focus:border-[#3d6e4e] focus:ring-2 focus:ring-[#3d6e4e]/20 transition-shadow';
  const GIRDI = `${KONTROL} border-[#d0c4b0] w-full min-w-0 px-1.5`;
  const SAYI = `${GIRDI} text-right font-mono tabular-nums`;
  const SECIM = `${KONTROL} border-[#d0c4b0] w-full min-w-0 pl-1.5 pr-0.5`;
  const IPUCU = 'block mt-0.5 text-[10px] font-mono tabular-nums text-right leading-tight';
  const BASLIK = 'text-[#1a1916] font-semibold text-[11px] uppercase tracking-wide px-1.5 py-2 leading-tight';
  // Mantıksal grupların başladığı hücrelere ince ayraç: Miktar | Fiyat | Vergi-Depo | Tutar
  const GRUP = 'border-l border-[#e6dcc9]';

  // Bir kalemin GEÇERLİ KDV oranı: KALEMİN KENDİSİ, yoksa kart (varsayılan).
  //
  // KDV ORANI FATURANINDIR (kullanıcı, 2026-09-28). Aynı ürün markasına /
  // muhteviyatına göre farklı oranla alınabiliyor (hayvansal krema %5, tam
  // sütten %0, katkılıysa %10). Bir dönem kartta oran varsa KART kazanıyor ve
  // kutu kilitleniyordu: firmanın %10 kestiği fatura %5 yazılıyordu, kartın
  // oranı değişince de eski faturayı açıp kaydetmek onun KDV'sini değiştiriyordu.
  //
  // Gösterim ile hesap AYNI yerden okur — bir dönem gösterim karttan, hesap
  // kalemin kopyasından geliyordu ve ekranda "%20" yazarken hesap 0 kalıyordu.
  const etkinKdvOrani = useCallback((it) => {
    if (it?.kdv_rate !== '' && it?.kdv_rate != null) return String(it.kdv_rate);
    const kart = it?.matched_product_id
      ? products.find(x => x.id === it.matched_product_id) : null;
    if (kart && kart.kdv_rate != null && kart.kdv_rate !== '') return String(kart.kdv_rate);
    return '0';
  }, [products]);

  // KDV seçildiğinde: oran karttakinden FARKLIYSA kartın da değişip
  // değişmeyeceği SORULUR (kullanıcı kararı). Varsayılan "yalnızca bu fatura":
  // pencere kapatılırsa kart değişmez. Kart değişikliği KAYITTA uygulanır —
  // fatura vazgeçilirse kart da değişmemiş olur.
  const kdvSec = (i, deger, kartUrun, kartOrani) => {
    setItems(onceki => onceki.map((x, j) => (j === i
      ? { ...x, kdv_rate: deger, kdv_karta_yaz: false, _kdv_elle: true } : x)));
    if (kartOrani != null && deger !== '' && parseFloat(deger) !== parseFloat(kartOrani)) {
      setKdvSoru({ i, yeni: deger, kartOrani, urun: kartUrun?.name || '', kod: kartUrun?.code || '' });
    }
  };

  // KDV dahil/hariç hesaplama
  const calcKdv = (total_price, kdv_rate) => {
    const total = parseFloat(total_price) || 0;
    const rate = parseFloat(kdv_rate) || 0;
    if (form.kdv_inclusive) {
      // Fiyat KDV dahil → KDV içinden ayrıştır
      const net = total / (1 + rate / 100);
      return { net, kdv: total - net };
    } else {
      // Fiyat KDV hariç → KDV üstüne ekle
      return { net: total, kdv: total * rate / 100 };
    }
  };

  // SATIR TUTARI TEK KAYNAKTAN. Başlıktaki toplamlar bir zamanlar satırın TAM
  // hassasiyetli çarpımından, KDV matrahı ise satırın YUVARLANMIŞ tutarından
  // hesaplanıyordu: iskontosuz toplam 1.560,90 yazarken matrah 1.560,89
  // çıkıyordu (kullanıcı, 2026-09-21). Faturada her satır iki haneye
  // yuvarlanır ve toplam o satırların toplamıdır; üç rakam da buradan gelir.
  // (satirTutari: lib/faturaKalemi — satış faturasıyla ortak)

  const grossTotal = items.reduce((s, it) => s + satirTutari(it).brut, 0);
  const discountTotal = items.reduce((s, it) => s + satirTutari(it).iskonto, 0);
  // Matrah, KDV ve genel toplam KURUŞLU ve tek kuraldan: KDV yuvarlanmadan
  // eklendiği için toplam yarım kuruşlu saklanıyor, liste ile bu ekran bir
  // kuruş farklı gösteriyordu (bkz. lib/faturaKalemi.faturaToplamlari).
  const toplamlar = faturaToplamlari(items, etkinKdvOrani, form.kdv_inclusive);
  const itemsSubtotal = toplamlar.matrah;
  const itemsKdv = toplamlar.kdv;
  const genelToplam = toplamlar.genel;

  const handleCreateReceipt = async () => {
    if (!form.supplier_id) { toast.error('Önce tedarikçi seçin'); return; }
    // Depo kalem bazında olduğu için her depo için AYRI mal kabul oluşturulur
    const byWarehouse = {};
    items
      .filter(it => it.matched_product_id && it.warehouse_id && parseFloat(it.quantity) > 0)
      .forEach(it => {
        (byWarehouse[it.warehouse_id] ||= []).push({
          product_card_id: it.matched_product_id,
          product_name: it.matched_product_name || it.description,
          ordered_qty: 0,
          received_qty: parseFloat(it.quantity),
          unit: it.unit || 'adet',
          unit_price: parseFloat(it.unit_price) || 0,
        });
      });
    const warehouseIds = Object.keys(byWarehouse);
    if (!warehouseIds.length) { toast.error('Deposu seçilmiş ve miktarı girilmiş ürün bulunamadı'); return; }
    setReceiptLoading(true);
    try {
      let created = 0, totalItems = 0;
      for (const wid of warehouseIds) {
        await api.post('/purchase-receipts', {
          supplier_id: form.supplier_id,
          warehouse_id: wid,
          invoice_number: form.invoice_number,
          invoice_date: form.invoice_date,
          items: byWarehouse[wid],
          notes: form.notes,
        });
        created += 1;
        totalItems += byWarehouse[wid].length;
      }
      toast.success(created === 1
        ? `Mal kabul oluşturuldu (${totalItems} ürün) — onay bekliyor`
        : `${created} depo için mal kabul oluşturuldu (${totalItems} ürün) — onay bekliyor`);
      window.open(`/erp/purchasing?tab=receipts`, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mal kabul oluşturulamadı');
    }
    setReceiptLoading(false);
  };

  // ── Fatura silme: iki aşamalı onay ──
  const openDeleteDialog = async () => {
    setDeleteStep(1);
    setDeletePreview(null);
    setShowDelete(true);
    try {
      const res = await api.get(`/invoices/${editId}/delete-preview`);
      setDeletePreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silme önizlemesi alınamadı');
      setShowDelete(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`/invoices/${editId}`);
      const d = res.data;
      const parts = [];
      if (d.ledger_deleted) parts.push(`${d.ledger_deleted} cari kayıt`);
      if (d.movements_deleted) parts.push(`${d.movements_deleted} stok hareketi`);
      toast.success(`Fatura silindi${parts.length ? ` — ${parts.join(', ')} geri alındı` : ''}`);
      setShowDelete(false);
      navigate('/erp/invoices');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fatura silinemedi');
      setDeleting(false);
    }
  };

  const handleSubmit = async () => {
    // KDV dahil/hariç ZORUNLU.
    //
    // Sunucu bu alanı boş gelirse "hariç" olarak yazıyor. Eski faturalar
    // (11.08.2026 öncesi) bu seçim olmadan kaydedilmişti; biri başka bir şeyi
    // düzeltmek için faturayı açıp kaydettiğinde fatura sessizce "hariç"
    // damgalanıyor, üstelik bu yanlış olabiliyordu. Seçim açıkça istenir.
    if (form.kdv_inclusive === undefined) {
      toast.error('KDV dahil mi hariç mi seçilmeden fatura kaydedilemez.', { duration: 6000 });
      document.querySelector('[data-testid="kdv-haric"]')?.scrollIntoView({ block: 'center' });
      document.querySelector('[data-testid="kdv-haric"]')?.focus();
      return;
    }
    // Tedarikçi ZORUNLU — seçilmezse fatura hiçbir cari hesaba işlenmez.
    // Ekranda AI'nin okuduğu ham isim görünse bile bu gerçek bir cari bağlantısı değildir.
    if (!form.supplier_id) {
      toast.error(
        form.parsed_supplier_name
          ? `Cari hesap seçilmeli. Faturadan okunan "${form.parsed_supplier_name}" ismi yalnızca metindir — listeden gerçek cariyi seçin.`
          : 'Cari hesap (tedarikçi) seçilmeden fatura kaydedilemez.',
        { duration: 6000 }
      );
      document.querySelector('[data-testid="invoice-supplier"]')?.focus();
      return;
    }
    // AMBALAJ BİRİMİ ZORUNLU (stoklu kalemlerde).
    //
    // Ambalaj üçlüsü birlikte anlam taşıyor: "2 paket × 30 = 60 adet". Birim
    // boş kalınca satırın ne aldığımızı söylemesi imkânsızlaşıyor ve ekranda
    // boş sütun olarak kalıyor (2026-09'da 65 kalem böyle birikmişti).
    // Sunucu da reddediyor; buradaki kontrol kullanıcıyı doğrudan satıra
    // götürmek için — hata mesajını beklemek yerine.
    const birimsiz = items
      .map((it, i) => ({ it, i }))
      .filter(({ it }) => it.matched_product_id && !(it.unit || '').trim());
    if (birimsiz.length) {
      const adlar = birimsiz.slice(0, 3)
        .map(({ it }) => it.matched_product_name || it.description || 'kalem');
      toast.error(
        `Ambalaj birimi girilmemiş ${birimsiz.length} kalem var: ${adlar.join(', ')}` +
        (birimsiz.length > 3 ? ` ve ${birimsiz.length - 3} kalem daha` : '') +
        '. Faturadaki birimi seçin.',
        { duration: 7000 });
      const ilk = document.querySelector(`[data-testid="item-unit-${birimsiz[0].i}"]`);
      ilk?.scrollIntoView({ block: 'center' });
      ilk?.focus();
      return;
    }
    // AÇIK MAL KABUL UYARISI.
    //
    // Varsayılan akış "irsaliyeli fatura": fatura tek başına stok yazıyor. Bu
    // cariden teslimatı girilmiş ama faturalanmamış mal varsa ve kullanıcı
    // bağlamadan kaydederse aynı mal İKİ KEZ stoğa girer. Engellemiyoruz —
    // gerçekten ayrı bir alım olabilir — ama sessiz de kalmıyoruz.
    const baglanmamis = acikKalemler.length;
    const baglanan = items.filter(it => it.teslimat_ref?.belge_id).length;
    if (baglanmamis > 0 && baglanan === 0) {
      const onay = window.confirm(
        `Bu cariden faturası bekleyen ${baglanmamis} mal kabul kalemi var.\n\n` +
        'Bu fatura o teslimatları kapatıyorsa "Mal Kabul Bağla" ile bağlayın; ' +
        'bağlamazsanız aynı mal stoğa İKİNCİ KEZ girer.\n\n' +
        'Bağlamadan kaydetmek istiyor musunuz?');
      if (!onay) {
        setBaglamaAcik(true);
        return;
      }
    }
    // MÜKERRER KONTROLÜ — kayıt öncesi son kapı.
    //
    // Sistem aynı faturanın ikinci kez girilmesine hiç ses çıkarmıyordu;
    // 2026-09-12'de 33 grup / 35 fazla kayıt bu yüzden birikmişti. Artık her
    // fatura KENDİ cari kaydını açtığı için mükerrer giriş cariyi çift
    // borçlandırıyor ve stoğu iki kez artırıyor — uyarı olmadan fark edilmesi
    // çok zor. ENGELLEMİYORUZ: aynı numaralı iki ayrı fatura gerçekten
    // olabiliyor, kararı kullanıcı verir.
    try {
      const { data } = await api.get('/invoices/mukerrer-kontrol', {
        params: {
          supplier_id: form.supplier_id,
          invoice_number: form.invoice_number || '',
          invoice_date: form.invoice_date || '',
          amount: genelToplam || 0,
          haric_id: isEdit ? editId : '',
        },
      });
      const eslesmeler = data?.eslesmeler || [];
      if (eslesmeler.length) {
        const liste = eslesmeler.map(e => (
          `• ${e.invoice_date}   No: ${e.invoice_number || '(boş)'}   ` +
          `${(e.amount_try || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL   ` +
          `${e.kalem} kalem\n     └ ${e.eslesme === 'numara'
            ? 'aynı fatura numarası' : 'aynı tarih ve aynı tutar'}`
        )).join('\n');
        const onay = window.confirm(
          `Bu cariye ait benzer fatura zaten kayıtlı:\n\n${liste}\n\n` +
          'Aynı faturayı ikinci kez kaydederseniz cari hesap ÇİFT borçlanır ' +
          've stok iki kez artar.\n\nYine de kaydetmek istiyor musunuz?'
        );
        if (!onay) return;
      }
    } catch (e) {
      // Kontrol başarısız olursa kaydı ENGELLEME: uyarı bir kolaylık,
      // kaydetmenin önkoşulu değil.
      console.warn('Mükerrer kontrolü yapılamadı:', e);
    }
    setLoading(true);
    const payload = {
      ...form,
      status: 'completed',
      invoice_type: form.invoice_type,
      amount_foreign: genelToplam,
      kdv_amount: itemsKdv,
      exchange_rate: parseFloat(form.exchange_rate) || 1,
      image_data: imagePreview,
      items: items.map(it => ({
        description: it.description, quantity: parseFloat(it.quantity) || 0,
        unit: it.unit, unit_price: parseFloat(it.unit_price) || 0,
        discount_rate: parseFloat(it.discount_rate) || 0,
        package_qty: parseFloat(it.package_qty) || null,
        package_unit: it.package_unit || null,
        // Mal kabul bağı: bu kalem hangi teslimat belgesinin hangi satırını
        // kapatıyor. Sunucu bunu görünce kalemde STOK YAZMAZ (mal zaten
        // teslimat belgesiyle stoğa girdi) ve belgedeki `faturalanan_miktar`ı
        // artırır. Alan boşsa fatura bugünkü gibi davranır.
        teslimat_ref: it.teslimat_ref || null,
        total_price: parseFloat(it.total_price) || (parseFloat(it.quantity || 0) * parseFloat(it.unit_price || 0) * (1 - (parseFloat(it.discount_rate) || 0) / 100)),
        // Ekranda gösterilen oranla kaydedilen oran ayni olmali (bkz. etkinKdvOrani)
        kdv_rate: parseFloat(etkinKdvOrani(it)) || 0,
        // Oran AÇIKÇA seçildi mi? Kartında oran olmayan ürünlerde sunucu bu
        // işareti görürse seçilen oranı ürün kartına da yazar. İşaret olmadan
        // yazsaydı, dokunulmamış satırlar kartlara %0 damgalardı.
        // Yalnızca ELLE seçilen oran öğrenilir: firma geçmişinden gelen öneri ya
        // da eski faturadan yüklenen oran kartı kendiliğinden doldurmasın.
        kdv_secildi: !!it._kdv_elle && it.kdv_rate !== '' && it.kdv_rate != null,
        // Oran karttakinden farklı ve kullanıcı "kartı da güncelle" dedi
        kdv_karta_yaz: !!it.kdv_karta_yaz,
        // Kalem TEK türde kaydedilir. Türü dışındaki eşleşmeler gönderilmez;
        // aksi halde daha önce otomatik eşleşmiş bir ürün, kalem gidere
        // çevrildikten sonra da kayıtta kalıyordu (ekranda görünmeden).
        // DİKKAT: `_type` yalnızca kullanıcı türü değiştirdiğinde dolu olur;
        // AI okuması ve yeni satır ekleme akışlarında boştur. Bu yüzden tür,
        // alan yoksa kalemin kendi eşleşmelerinden TÜRETİLİR — aksi halde
        // ilk kayıtta tüm eşleşmeler ve depolar siliniyordu.
        ...(() => {
          const tur = it._type
            || (it.matched_expense_id ? 'expense'
              : it.matched_fixed_asset_type_id ? 'fixed_asset' : 'product');
          return {
            matched_product_id: tur === 'product' ? (it.matched_product_id || null) : null,
            matched_product_name: tur === 'product' ? (it.matched_product_name || null) : null,
            matched_product_code: tur === 'product' ? (it.matched_product_code || null) : null,
            // Depo kalem bazında — bir faturanın kalemleri farklı depolara girebilir
            warehouse_id: tur === 'product' ? (it.warehouse_id || null) : null,
            matched_expense_id: tur === 'expense' ? (it.matched_expense_id || null) : null,
            matched_fixed_asset_type_id: tur === 'fixed_asset' ? (it.matched_fixed_asset_type_id || null) : null,
          };
        })(),
      })),
      confidence_scores: scores
    };
    try {
      // Sunucu, kartında KDV oranı olmayan ürünlere faturadaki oranı işler;
      // sessiz kalmasın, hangi kartların güncellendiği söylensin.
      const ogrenildiBildir = (res) => {
        const o = res?.data?.kdv_ogrenildi;
        if (o?.length) {
          toast.success(
            `${o.length} ürün kartına KDV oranı işlendi: ${o.slice(0, 3).join(', ')}`
            + (o.length > 3 ? ` +${o.length - 3} ürün` : ''),
            { duration: 7000 });
        }
        const g = res?.data?.kdv_kart_guncellendi;
        if (g?.length) {
          toast.success(
            `${g.length} ürün kartının KDV oranı güncellendi: ${g.slice(0, 3).join(', ')}`
            + (g.length > 3 ? ` +${g.length - 3} ürün` : ''),
            { duration: 8000 });
        }
        // Kart oranı değiştiyse açık listeler yeni varsayılanı görsün
        if (o?.length || g?.length) refreshProducts?.();
      };
      // Kutu belgesi ANCAK kayit basariyla olustuktan sonra islenmise gecer
      const kutuyuKapat = async (kayitId, kayitNo) => {
        if (!kutuBelgesi) return;
        try {
          await api.post(`/dosya-kutusu/${kutuBelgesi.id}/islendi`, {
            kayit_turu: 'fatura', kayit_id: kayitId, kayit_no: kayitNo || '' });
          toast.success('Belge dosya kutusundan kaldırıldı');
        } catch {
          // Fatura kaydedildi ama belge tasinamadi — kullaniciyi uyar,
          // kutuda kalsin ki elle kapatabilsin.
          toast.warning('Fatura kaydedildi ama belge kutuda kaldı — elle işaretleyin');
        }
      };

      if (isEdit) {
        const res = await api.patch(`/invoices/${editId}`, payload);
        toast.success('Fatura güncellendi');
        ogrenildiBildir(res);
        await kutuyuKapat(editId, form.invoice_number);
        duzenlemeBitti(`/erp/invoices/${editId}`);
      } else {
        const res = await api.post('/invoices', { id: draftId || undefined, ...payload });
        toast.success('Fatura kaydedildi');
        ogrenildiBildir(res);
        await kutuyuKapat(res.data?.id || draftId, form.invoice_number);
        navigate('/erp/invoices');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
    setLoading(false);
  };

  const ConfBadge = ({ field }) => {
    if (!scores || !scores[field]) return null;
    const c = scores[field];
    return <span className={`status-badge text-xs ml-2 ${confColors[c]}`}>{c === 'high' ? 'Net' : c === 'medium' ? 'Belirsiz' : 'Dusuk'}</span>;
  };

  return (
    <div data-testid="invoice-new-page">
      {/* ── Başlık + AI Yükleme (tek satır) ── */}
      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-3">
        <Button variant="ghost" size="icon" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916] shrink-0" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-base md:text-xl font-bold text-[#1a1916] tracking-tight shrink-0">{isEdit ? 'Fatura Düzenle' : 'Yeni Fatura'}</h1>
        <div className="flex-1" />
        {!isEdit && (
          <>
            <Button onClick={kutuyuAc} disabled={parsing} size="sm" variant="outline"
              className="border-[#3d6e4e] text-[#3d6e4e] hover:bg-[#3d6e4e]/10 text-xs font-semibold h-11 md:h-8 px-4 md:px-3"
              data-testid="kutudan-sec-btn">
              <Inbox size={13} className="mr-1.5" /> Kutudan Seç
            </Button>
            <Button onClick={() => setShowUploadModal(true)} disabled={parsing} size="sm"
              className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] text-xs font-semibold h-11 md:h-8 px-4 md:px-3" data-testid="upload-invoice-btn">
              {parsing ? <><Sparkles size={13} className="mr-1.5 animate-spin" />Okunuyor...</> : <><Upload size={13} className="mr-1.5" />AI ile Oku</>}
            </Button>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.pdf" onChange={handleFileParse} className="hidden" />
          </>
        )}
        {isEdit && silmeYetkisi && (
          <Button onClick={openDeleteDialog} disabled={loading || deleting} size="sm" variant="outline"
            className="border-[#c98d8d] text-[#7a3535] hover:bg-[#7a3535]/10 hover:border-[#7a3535] font-semibold h-11 md:h-8 px-4 md:px-3 text-xs"
            data-testid="delete-invoice-btn">
            <Trash2 size={13} className="mr-1.5" /> Sil
          </Button>
        )}
        <Button onClick={handleSubmit} disabled={loading} size="sm"
          className="hidden md:inline-flex bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold h-8 px-4 text-xs" data-testid="save-invoice-btn">
          <Save size={13} className="mr-1.5" /> {loading ? (isEdit ? 'Güncelleniyor...' : 'Kaydediliyor...') : (isEdit ? 'Kaydet' : 'Kaydet')}
        </Button>
      </div>

      {/* Kutudan gelen belge: nereden geldigi ve vazgecme yolu gorunur olsun */}
      {kutuBelgesi && (
        <div className="mb-3 flex flex-wrap items-center gap-2 bg-[#3d6e4e]/5 border border-[#3d6e4e]/30 rounded-lg p-2 text-sm"
          data-testid="kutu-belge-serit">
          <Inbox size={15} className="text-[#3d6e4e] shrink-0" />
          <span className="text-[#3F3E39]">
            Dosya kutusundan: <strong>{kutuBelgesi.tur_adi}</strong>
            {kutuBelgesi.sayfa_sayisi > 1 && ` · ${kutuBelgesi.sayfa_sayisi} sayfanın tamamı okundu`}
            {' · '}{kutuBelgesi.yukleyen_adi}
          </span>
          <span className="text-xs text-[#8a7e70]">
            {saklandi
              ? `Girdikleriniz saklandı ${new Date(saklandi).toLocaleTimeString('tr-TR',
                  { hour: '2-digit', minute: '2-digit' })} — sayfadan çıksanız da durur`
              : 'Kaydedince kutudan düşer'}
          </span>
          {/* Yeniden okutmak PARA VE ZAMAN harcar, ustelik girilmis her seyi
              siler; bu yuzden bilerek secilen bir eylem. */}
          <button type="button" onClick={yenidenOku} disabled={parsing}
            className={`ml-auto text-xs hover:underline disabled:opacity-40 ${
              okumaOnayi ? 'text-[#7a3535] font-semibold' : 'text-[#3d6e4e]'}`}
            data-testid="kutu-yeniden-oku">
            {okumaOnayi ? 'Girilenler silinecek — yine de oku' : 'Yeniden oku'}
          </button>
          <button type="button" onClick={kutuyaGeriBirak}
            className="text-xs text-[#7a3535] hover:underline"
            data-testid="kutuya-geri-birak">
            Vazgeç, kutuya geri bırak
          </button>
        </div>
      )}

      {/* Düzenlemede belge kartı: sayfaları gösterir, yenisini EKLETİR
          (Bilnex'ten gelen faturaların belgesi yoktu, eklemenin yolu da yoktu
          — kullanıcı, 2026-10-01). Eski `image_data` yedek olarak görünür. */}
      {isEdit && (
        <div className="mb-3 [&>.erp-card]:mt-0">
          <FaturaBelgesi invoiceId={editId} imageData={imagePreview} duzenlenebilir />
        </div>
      )}

      {/* AI önizleme (sadece görsel yüklendiyse) — yeni faturada */}
      {!isEdit && imagePreview && (
        <div className="mb-3 flex flex-col sm:flex-row items-start gap-2 sm:gap-3 bg-[#f5f0e8] border border-[#d0c4b0] rounded-lg p-2">
          <img src={imagePreview} alt="Fatura" onClick={() => setShowImageModal(true)}
            className="max-h-40 sm:max-h-24 w-full sm:w-auto rounded border border-[#d0c4b0] object-contain cursor-zoom-in hover:opacity-80 transition-opacity" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#3d6e4e]">AI okudu</p>
            {form.parsed_supplier_name && <p className="text-xs text-[#8a7e70] truncate">Tedarikçi: {form.parsed_supplier_name}</p>}
            <button onClick={() => setShowImageModal(true)} className="text-xs text-[#3d6e4e] hover:underline mt-0.5">Büyüt →</button>
          </div>
          <button onClick={() => setImagePreview(null)} className="text-[#8a7e70] hover:text-[#7a3535] p-1"><X size={14} /></button>
        </div>
      )}

      <div className="space-y-3">
        {/* ── Kompakt Fatura Bilgileri ── */}
        <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-3">
          {/* Satır 1: Tedarikçi + Fatura Türü + Sorumluluk Merkezi + Fatura No
              (md gridi 12 sütun; span toplamı 12'yi AŞMAMALI, yoksa alanlar
              alt satıra sarıp formu iki kat uzatıyor) */}
          <div className="grid grid-cols-2 md:grid-cols-12 gap-2 mb-2">
            <div className="col-span-2 md:col-span-4 min-w-0">
              <p className={ALAN_ETIKET}>Tedarikçi <ConfBadge field="supplier" /></p>
              {form.parsed_supplier_name && !form.supplier_id && (
                <p className="text-[10px] text-[#7a6a2e] mb-0.5">AI: "{form.parsed_supplier_name}"</p>
              )}
              <SupplierCombobox
                value={form.supplier_id}
                onChange={(v) => setForm(p => ({ ...p, supplier_id: v }))}
                suppliers={suppliers}
              />
              {!form.supplier_id && (
                <button type="button" onClick={openNewSupplier}
                  className="text-[10px] text-[#3d6e4e] hover:underline flex items-center gap-0.5 mt-0.5" data-testid="new-supplier-from-invoice">
                  <ExternalLink size={10} /> Yeni tedarikçi oluştur
                </button>
              )}
            </div>
            <div className="col-span-2 md:col-span-2 min-w-0">
              <p className={ALAN_ETIKET}>Fatura Türü</p>
              <Select value={form.invoice_type} onValueChange={(v) => setForm(p => ({ ...p, invoice_type: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0" data-testid="invoice-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {invoiceTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <MerkezSecici
              value={form.cost_center_id}
              onChange={(v) => setForm(p => ({ ...p, cost_center_id: v }))}
              label="Sorumluluk Merkezi"
              className="col-span-2 md:col-span-3"
              etiketClass={ALAN_ETIKET}
              tetikClass="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0"
            />
            <div className="col-span-2 md:col-span-3 min-w-0">
              <p className={ALAN_ETIKET}>Fatura No <ConfBadge field="invoice_number" /></p>
              <Input value={form.invoice_number} onChange={(e) => setForm(p => ({ ...p, invoice_number: e.target.value }))}
                className="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0" data-testid="invoice-number" />
            </div>
          </div>
          {/* Satır 2: Tarih + Vade Günü + Vade Tarihi + PB + Kur + Depo */}
          <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
            <div className="col-span-1 md:col-span-2 min-w-0">
              <p className={ALAN_ETIKET}>Fatura Tarihi <ConfBadge field="invoice_date" /></p>
              <Input type="date" value={form.invoice_date} onChange={(e) => setForm(p => ({ ...p, invoice_date: e.target.value }))}
                className="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0" data-testid="invoice-date" />
            </div>
            <div className="col-span-1 md:col-span-1 min-w-0">
              <p className={ALAN_ETIKET}>Vade (gün)</p>
              <Input type="number" value={form.due_days} onChange={(e) => setForm(p => ({ ...p, due_days: e.target.value }))}
                placeholder="30" className="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0" data-testid="invoice-due-days" />
            </div>
            <div className="col-span-1 md:col-span-2 min-w-0">
              <p className={ALAN_ETIKET}>Vade Tarihi</p>
              <Input type="date" value={form.due_date} onChange={(e) => setForm(p => ({ ...p, due_date: e.target.value }))}
                className="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0" data-testid="invoice-due-date" />
            </div>
            <div className="col-span-1 md:col-span-1 min-w-0">
              <p className={ALAN_ETIKET}>PB</p>
              <Select value={form.currency} onValueChange={(v) => setForm(p => ({ ...p, currency: v, exchange_rate: v === 'TRY' ? '1' : p.exchange_rate }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0" data-testid="invoice-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.currency !== 'TRY' && (
              <div className="col-span-1 md:col-span-1 min-w-0">
                <p className={ALAN_ETIKET}>Kur</p>
                <Input type="number" step="0.0001" value={form.exchange_rate} onChange={(e) => setForm(p => ({ ...p, exchange_rate: e.target.value }))}
                  className="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0" data-testid="invoice-rate" />
              </div>
            )}
            <div className={form.currency !== 'TRY' ? 'col-span-2 md:col-span-2' : 'col-span-2 md:col-span-3'}>
              <p className={ALAN_ETIKET}>Depo</p>
              <div className="h-9 flex items-center text-xs text-[#8a7e70]">
                {usedWarehouses.length === 0
                  ? <span className="italic">Kalem bazında seçilir</span>
                  : <span className="text-[#1a1916] font-medium truncate" title={usedWarehouses.map(w => w.name).join(', ')}>
                      <Package size={11} className="inline mr-1 -mt-0.5" />
                      {usedWarehouses.length === 1 ? usedWarehouses[0].name : `${usedWarehouses.length} depo`}
                    </span>}
              </div>
              {usedWarehouses.length > 0 && (
                <button type="button" onClick={handleCreateReceipt} disabled={receiptLoading}
                  className="mt-1 text-[10px] text-[#3d6e4e] hover:underline flex items-center gap-0.5 disabled:opacity-50">
                  <Package size={10} /> {receiptLoading ? 'Oluşturuluyor...' : 'Mal kabulü oluştur'}
                </button>
              )}
            </div>
            {/* KDV dahil/hariç — ÜÇ durum: hariç, dahil, HENÜZ SEÇİLMEMİŞ.
                Seçilmemiş hâl eskiden "Hariç" gibi görünüyordu; kullanıcı
                faturayı açınca işaretli sanıyordu. Artık hiçbiri yanmaz ve
                altında uyarı çıkar. */}
            <div className="col-span-1 md:col-span-2 min-w-0">
              <p className={ALAN_ETIKET}>KDV</p>
              <div className={`flex h-11 md:h-9 rounded-md border overflow-hidden text-sm
                ${form.kdv_inclusive === undefined ? 'border-[#e0b45a] border-2' : 'border-[#d0c4b0]'}`}>
                <button
                  type="button"
                  onClick={() => setForm(p => ({ ...p, kdv_inclusive: false }))}
                  className={`flex-1 text-xs font-medium transition-colors ${form.kdv_inclusive === false ? 'bg-[#8a7e70] text-white' : 'bg-white text-[#8a7e70] hover:bg-[#f5f0ea]'}`}
                  data-testid="kdv-haric"
                >
                  Hariç
                </button>
                <button
                  type="button"
                  onClick={() => setForm(p => ({ ...p, kdv_inclusive: true }))}
                  className={`flex-1 text-xs font-medium transition-colors ${form.kdv_inclusive === true ? 'bg-[#8a7e70] text-white' : 'bg-white text-[#8a7e70] hover:bg-[#f5f0ea]'}`}
                  data-testid="kdv-dahil"
                >
                  Dahil
                </button>
              </div>
              {form.kdv_inclusive === undefined && (
                <p className="mt-1 text-[10px] text-[#8a5c00] leading-tight" data-testid="kdv-isaretsiz">
                  İşaretlenmemiş — kaydetmeden önce seçin
                </p>
              )}
              {/* Öneri, tedarikçinin geçmiş faturalarından geliyor; kullanıcı
                  değiştirirse öneri notu kalkar, seçim kullanıcınındır. */}
              {kdvOnerisi && form.kdv_inclusive === kdvOnerisi.dahil && (
                <p className="mt-1 text-[10px] text-[#3d6e4e] leading-tight" data-testid="kdv-onerisi">
                  Bu firmanın önceki {kdvOnerisi.dayanak} faturasına göre işaretlendi
                </p>
              )}
            </div>
            <div className="col-span-2 md:col-span-1 min-w-0">
              <p className={ALAN_ETIKET}>Notlar</p>
              <Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Opsiyonel not..." className="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0" data-testid="invoice-notes" />
            </div>
          </div>
        </div>

        {/* Items */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#d0c4b0]">
            <span className="text-sm font-bold text-[#1a1916]">Fatura Kalemleri <span className="text-xs font-normal text-[#8a7e70]">({items.length} kalem)</span></span>
            <div className="flex items-center gap-1.5">
              {/* Mal kabul bağlama — yalnızca bu cariden faturası bekleyen
                  kalem varsa görünür; yoksa ekranı kalabalıklaştırmasın. */}
              {acikKalemler.length > 0 && (
                <Button type="button" size="sm" variant="outline"
                  onClick={() => setBaglamaAcik(a => !a)}
                  className="h-7 text-xs px-3 border-[#7a6a2e] text-[#7a6a2e] hover:bg-[#7a6a2e]/10"
                  data-testid="mal-kabul-bagla">
                  <Link2 size={12} className="mr-1" />
                  Mal Kabul Bağla ({acikKalemler.length})
                </Button>
              )}
              <Button type="button" size="sm" onClick={addItem} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] h-7 text-xs px-3" data-testid="add-invoice-item">
                <Plus size={12} className="mr-1" /> Kalem Ekle
              </Button>
            </div>
          </div>

          {baglamaAcik && acikKalemler.length > 0 && (
            <div className="border-b border-[#d0c4b0] bg-[#fdfbf6] px-3 py-2.5">
              <p className="text-xs text-[#6b5f4f] mb-2">
                Bu cariden <strong>faturası bekleyen</strong> mal kabul kalemleri.
                Eklediğiniz kalem stoğa <em>ikinci kez</em> girmez — mal zaten
                teslimat belgesiyle girdi.
              </p>
              <div className="overflow-x-auto border border-[#e8dfd0] rounded bg-white">
                <table className="w-full text-xs min-w-[560px]">
                  <thead className="bg-[#f5f1e8] text-[10px] uppercase text-[#8a7e70]">
                    <tr>
                      <th className="text-left p-1.5">Belge</th>
                      <th className="text-left p-1.5">Ürün</th>
                      <th className="text-right p-1.5">Kalan</th>
                      <th className="text-right p-1.5">Fiyat</th>
                      <th className="p-1.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {acikKalemler.map((tk, i) => (
                      <tr key={`${tk.belge_id}-${tk.kalem_id}`}
                        className="border-t border-[#e8dfd0]">
                        <td className="p-1.5 text-[#6b5f4f]">
                          {tk.belge_no || '(no yok)'}
                          <span className="block text-[10px] text-[#8a7e70]">
                            {tk.belge_turu_adi} · {tk.teslim_tarihi}
                          </span>
                        </td>
                        <td className="p-1.5 text-[#1a1916]">
                          {tk.matched_product_name || tk.description}
                        </td>
                        <td className="p-1.5 text-right font-mono">
                          {tk.kalan_miktar} {tk.unit}
                        </td>
                        <td className="p-1.5 text-right font-mono text-[#6b5f4f]">
                          {tk.unit_price != null
                            ? Number(tk.unit_price).toLocaleString('tr-TR') : '—'}
                        </td>
                        <td className="p-1.5 text-right">
                          <Button type="button" size="sm" variant="ghost"
                            onClick={() => teslimattanEkle(tk)}
                            className="h-6 text-[11px] text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
                            data-testid={`teslimat-ekle-${i}`}>
                            Faturaya ekle
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <CardContent className="p-0 md:overflow-x-auto">
            {/* Mobilde tablo blok duzene gecer: her kalem bir kart olur.
                Masaustunde eski sabit genislikli tablo aynen kalir. */}
            <table className="w-full block md:table md:min-w-[1360px] md:table-fixed">
              {/* Sütun genişlikleri TEK yerde. Dar bırakılan sütunlarda değerler
                  kırpılıyordu (KDV kutusunda "%0", depo seçiminde metin).
                  Yalnızca "Eşleşme" esnek bırakıldı: içinde iki kontrol var ve
                  ürün adları uzun; artan genişliği o sütun emsin. */}
              <colgroup className="hidden md:table-column-group">
                <col className="w-[240px]" />
                <col className="w-[92px]" />
                <col />
                <col className="w-[84px]" />
                <col className="w-[84px]" />
                <col className="w-[104px]" />
                <col className="w-[104px]" />
                <col className="w-[68px]" />
                <col className="w-[84px]" />
                <col className="w-[124px]" />
                <col className="w-[108px]" />
                <col className="w-[40px]" />
              </colgroup>
              <thead className="hidden md:table-header-group sticky top-0 z-10">
                <tr className="bg-[#e8dfd0]">
                  {/* Başlık hizası hücre hizasıyla aynı: sayılar sağda, metin solda */}
                  <th className={`${BASLIK} text-left`}>Ürün Adı</th>
                  <th className={`${BASLIK} text-left`}>Tür</th>
                  <th className={`${BASLIK} text-left`}>Eşleşme</th>
                  <th className={`${BASLIK} text-right ${GRUP}`} title="Ambalaj adedi">Amb.<br />Adedi</th>
                  <th className={`${BASLIK} text-left`} title="Ambalaj birimi">Amb.<br />Birimi</th>
                  <th className={`${BASLIK} text-right`} title="Bir ambalajın içindeki ürün miktarı">Amb.<br />İçeriği</th>
                  <th className={`${BASLIK} text-right ${GRUP}`}>Birim Fiyat</th>
                  <th className={`${BASLIK} text-right`}>İsk. %</th>
                  <th className={`${BASLIK} text-left ${GRUP}`}>KDV %</th>
                  <th className={`${BASLIK} text-left`}>Depo</th>
                  <th className={`${BASLIK} text-right ${GRUP}`}>Tutar</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="block md:table-row-group">
                {items.map((it, i) => {
                  const lineTotal = parseFloat(it.total_price) || 0;
                  const { net: lineNet, kdv: lineKdv } = calcKdv(it.total_price, etkinKdvOrani(it));
                  const itemType = it._type || 'product';
                  // Kalem bir sey ile ESLESMEDI mi? Uyari rengi bunun icin.
                  // NOT: demirbas eslesmesi eskiden hesaba katilmiyordu; eslesmis
                  // bir demirbas kalemi de "eslesmemis" gibi sari gorunuyordu.
                  const eslesmedi = !it.matched_product_id && !it.matched_expense_id
                    && !it.matched_fixed_asset_type_id;
                  // ZEBRA ile UYARI ayri kanallardan gider.
                  //
                  // Ikisi de arka plani kullaninca catisiyorlardi: gecmisi
                  // olmayan bir tedarikcide kalemlerin TAMAMI eslesmiyor
                  // (ornek: 15/15), butun kartlar uyari rengine boyaniyor ve
                  // zebra tamamen kayboluyordu. Artik arka plan YALNIZCA
                  // zebranin, uyari ise sol kenar cizgisi + cerceve + etiketle
                  // veriliyor; hangi kalem eslesmezse eslesmesin zebra duruyor.
                  const rowBg = i % 2 === 0 ? 'bg-white' : 'bg-[#f3eee4]';
                  // Sol kenar vurgusu: border yardimcilariyla catismasin diye
                  // ic golge olarak veriliyor (mobilde de masaustunde de gecerli).
                  const uyariCizgi = eslesmedi ? 'shadow-[inset_4px_0_0_0_#e0b45a]' : '';
                  const kartCerceve = eslesmedi ? 'border-[#e0b45a]' : 'border-[#d0c4b0]';
                  return (
                      <tr key={i} className={`${rowBg} ${uyariCizgi} grid grid-cols-6 gap-x-1.5 gap-y-0.5 md:table-row
                        border ${kartCerceve} rounded-lg mb-2 p-2 mx-2
                        md:border-0 md:border-b md:rounded-none md:mb-0 md:p-0 md:mx-0
                        hover:bg-[#e8dfd0]/50 transition-colors`}>
                        {/* Mobilde kartin ustunde satir numarasi + tutar */}
                        <td className="md:hidden order-1 col-span-6 flex items-center justify-between mb-1 pb-1 border-b border-[#e6dcc9]">
                          <span className="text-[11px] font-semibold text-[#8a7e70]">
                            Kalem {i + 1}
                            {eslesmedi && (
                              <span className="ml-1.5 text-[#8a5c00] font-bold" data-testid={`item-unmatched-${i}`}>
                                · eşleşmedi
                              </span>
                            )}
                          </span>
                          <span className="font-mono tabular-nums text-sm font-semibold text-[#1a1916]">
                            {fmtTR(lineTotal)}
                          </span>
                        </td>
                        {/* Ürün Adı (faturadaki metin) + yeniden eşleştir */}
                        <td className={`${HUCRE} order-2 col-span-6`}>
                          <span className={ETIKET}>Ürün Adı</span>
                          <div className="flex items-center gap-1">
                            <Input value={it.description || ''} onChange={(e) => updateItem(i, 'description', e.target.value)}
                              placeholder="Kalem açıklaması" className={`${GIRDI} flex-1`} data-testid={`item-desc-${i}`} />
                            <button type="button" onClick={() => rematchItem(i)} title="Açıklamaya göre yeniden eşleştir"
                              aria-label="Açıklamaya göre yeniden eşleştir"
                              className="h-11 w-11 md:h-8 md:w-8 flex items-center justify-center rounded-md border border-[#d0c4b0] bg-white text-[#8a7e70] hover:text-[#1a1916] hover:border-[#8a7e70] shrink-0 transition-colors focus:outline-none focus:ring-2 focus:ring-[#3d6e4e]/20">
                              <RefreshCw size={12} />
                            </button>
                          </div>
                        </td>
                        {/* Kalem türü — mobilde Depo ile aynı satırda durur.
                            Eskiden eşleşme kutusuyla TEK hücredeydi; hem
                            masaüstünde 240px'e sıkışıyordu hem de mobilde
                            depoyla yan yana getirilemiyordu. */}
                        <td className={`${HUCRE} order-3 ${itemType === 'expense' ? 'col-span-6' : 'col-span-3'}`}>
                          <span className={ETIKET}>Tür</span>
                          <select value={itemType} onChange={(e) => updateItem(i, '_type', e.target.value)}
                            title="Kalem türü"
                            className={`${SECIM} text-[11px]`}
                            data-testid={`item-type-${i}`}>
                            <option value="product">Stoklu</option>
                            <option value="expense">Gider</option>
                            <option value="fixed_asset">Demirbaş</option>
                          </select>
                        </td>
                        {/* Eşleşme: türe göre ürün / gider / demirbaş arama */}
                        <td className={`${HUCRE} order-5 col-span-6`}>
                          <span className={ETIKET}>Eşleşme</span>
                          <div className="flex items-center gap-1">
                            <div className="flex-1 min-w-0">
                              {itemType === 'product' ? (
                                <div className="flex items-center gap-1 min-w-0">
                                  <SearchableCombobox
                                    value={it.matched_product_id || ''}
                                    onChange={(v) => updateItem(i, 'matched_product_id', v)}
                                    options={products.map(p => ({ value: p.id, label: p.name, code: p.code }))}
                                    placeholder="Ürün seç veya ara..."
                                    searchPlaceholder="Ürün ara..." clearLabel="Eşleştirme"
                                    data-testid={`item-match-${i}`}
                                  />
                                  {!it.matched_product_id && (
                                    <button type="button" onClick={() => openNewProductForItem(i)}
                                      title="Yeni ürün kartı oluştur" aria-label="Yeni ürün kartı oluştur"
                                      className="h-11 w-11 md:h-8 md:w-8 flex items-center justify-center rounded-md text-[#3d6e4e] hover:bg-[#3d6e4e]/10 shrink-0 transition-colors focus:outline-none focus:ring-2 focus:ring-[#3d6e4e]/20"
                                      data-testid={`item-new-product-${i}`}>
                                      <ExternalLink size={12} />
                                    </button>
                                  )}
                                </div>
                              ) : itemType === 'expense' ? (
                                <SearchableCombobox
                                  value={it.matched_expense_id || ''}
                                  onChange={(v) => updateItem(i, 'matched_expense_id', v)}
                                  options={expenseItems.map(e => ({ value: e.id, label: e.name, code: e.code, category: e.category_name || e.category }))}
                                  placeholder="Gider kalemi seç" searchPlaceholder="Gider ara..."
                                  clearLabel="Eşleştirme" groupBy="category"
                                  data-testid={`item-expense-${i}`}
                                />
                              ) : (
                                <SearchableCombobox
                                  value={it.matched_fixed_asset_type_id || ''}
                                  onChange={(v) => updateItem(i, 'matched_fixed_asset_type_id', v)}
                                  options={fixedAssetTypes.map(t => ({ value: t.id, label: t.name, code: t.code }))}
                                  placeholder="Demirbaş türü seç" searchPlaceholder="Tür ara..."
                                  clearLabel="Eşleştirme"
                                  data-testid={`item-fixed-asset-${i}`}
                                />
                              )}
                            </div>
                          </div>
                        </td>
                        {/* Ambalaj Adedi — MIKTAR grubunun başı */}
                        <td className={`${HUCRE} ${GRUP} order-6 col-span-2`}>
                          <span className={ETIKET}>Ambalaj Adedi</span>
                          <Input type="number" step="0.01" min="0" value={it.quantity || ''} onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                            placeholder="0" className={SAYI} data-testid={`item-qty-${i}`} />
                        </td>
                        {/* Ambalaj Birimi: kayıtlı birimlerden dropdown */}
                        <td className={`${HUCRE} order-7 col-span-2`}>
                          <span className={ETIKET}>Ambalaj Birimi</span>
                          {units.length > 0 ? (() => {
                            // Ürün kartında DÖNÜŞÜMÜ TANIMLI birimler öne çıkarılır.
                            // Sebep işlevsel, kozmetik değil: tanımlı birim seçilince
                            // ambalaj içeriği karttan otomatik doluyor (updateItem'ın
                            // 'unit' dalı), tanımsız birimde kullanıcı içeriği elle
                            // girmek zorunda ve yanlış girerse stok doğrudan bozuluyor.
                            // Doğru seçeneği görünür kılmak hatayı kaynağında önlüyor.
                            const kart = satirKarti(it);
                            const tanimli = kart ? birimSecenekleri(kart, units) : [];
                            const tanimliSet = new Set(tanimli.map(o => trNorm(o.deger)));
                            const digerleri = units.filter(
                              u => !tanimliSet.has(trNorm(u.abbreviation || '')));
                            // Kayıtlı değer listedeki hiçbir seçeneğe birebir uymuyorsa
                            // (eski/Bilnex satırlarında "KG" gibi yazımlar var) select
                            // boş görünüp sessizce değer kaybettiriyordu — koruyalım.
                            const secenekler = [...tanimli.map(o => o.deger),
                                                ...digerleri.map(u => u.abbreviation)];
                            const kayipDeger = it.unit && !secenekler.includes(it.unit)
                              ? it.unit : null;
                            // Stoklu kalemde birim ZORUNLU — boşken kırmızı çerçeve,
                            // kaydetmeye çalışmadan görünsün.
                            const bosZorunlu = it._type === 'product' && it.matched_product_id
                              && !(it.unit || '').trim();
                            return (
                            <select value={it.unit || ''} onChange={(e) => updateItem(i, 'unit', e.target.value)}
                              className={`${SECIM} ${bosZorunlu ? 'border-[#7a3535] border-2 bg-[#fff0f0]' : ''}`}
                              title={bosZorunlu ? 'Faturadaki ambalaj birimini seçin — boş bırakılamaz' : ''}
                              data-testid={`item-unit-${i}`}>
                              <option value="">—</option>
                              {kayipDeger && <option value={kayipDeger}>{kayipDeger}</option>}
                              {tanimli.length > 0 && (
                                // Çarpan GRUP BAŞLIĞINDA değil, option metninde de
                                // DEĞİL: "★ adet · 0,125 kg" 121px sürüyor, bu sütun
                                // ise çok daha dar — kapalı select metni kırpıyordu
                                // (ölçüldü). Açık listede başlık genişleyebildiği için
                                // açıklama oraya, kapalıda görünmesi gereken kısa
                                // etiket option'a konuldu. Çarpan zaten seçimden
                                // hemen sonra Ambalaj İçeriği alanına dolarak görünür.
                                <optgroup label="★ Üründe tanımlı — içeriği otomatik dolar">
                                  {tanimli.map(o => (
                                    <option key={`t-${o.deger}`} value={o.deger}>
                                      ★ {o.deger}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                              {digerleri.length > 0 && (
                                <optgroup label={tanimli.length > 0 ? 'Diğer birimler' : 'Birimler'}>
                                  {digerleri.map(u => (
                                    <option key={u.id} value={u.abbreviation}>{u.abbreviation}</option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                            );
                          })() : (
                            <Input value={it.unit || ''} onChange={(e) => updateItem(i, 'unit', e.target.value)}
                              placeholder="Birim" className={GIRDI} data-testid={`item-unit-${i}`} />
                          )}
                        </td>
                        {/* Ambalaj İçeriği + toplam ürün miktarı ipucu.
                            Ürünün ana birimi ayrı bir sütun yerine bu kutunun
                            sağında duruyor: sayı ile birimi ayıran boş bir
                            sütun, satırı gereksiz genişletiyordu. */}
                        <td className={`${HUCRE} order-8 col-span-2`}>
                          <span className={ETIKET}>Ambalaj İçeriği</span>
                          {(() => {
                            // Ambalaj birimi ürünün ana biriminden farklıysa içerik ZORUNLU:
                            // girilmezse stoğa ambalaj adedi/birimi düşüyor (ör. "15 adet"
                            // ekmek, ürün kg cinsindeyken). Backend de bu durumda kaydı reddeder.
                            const eksik = it._type === 'product' && it.matched_product_id
                              && it.package_unit && it.unit
                              && it.unit.toLowerCase() !== it.package_unit.toLowerCase()
                              && !(parseFloat(it.package_qty) > 0);
                            return (
                              <>
                                <div className="flex items-center gap-1.5">
                                  <Input type="number" step="0.001" min="0" value={it.package_qty || ''}
                                    onChange={(e) => updateItem(i, 'package_qty', e.target.value)}
                                    placeholder={eksik ? 'zorunlu' : '0'}
                                    title={eksik ? `1 ${it.unit} kaç ${it.package_unit}? Girilmezse stok yanlış birime düşer.` : ''}
                                    className={`${SAYI} ${eksik ? 'border-[#7a3535] border-2 bg-[#fff0f0]' : ''}`}
                                    data-testid={`item-pkg-qty-${i}`} />
                                  <span className="shrink-0 w-8 text-xs text-[#8a7e70] text-left"
                                    title="Ürünün kayıtlı ana birimi" data-testid={`item-pkg-unit-${i}`}>
                                    {satirKarti(it)?.unit || it.package_unit || '—'}
                                  </span>
                                </div>
                                {eksik && (
                                  <span className={`${IPUCU} text-[#7a3535] font-semibold pr-9`}>
                                    1 {it.unit} = ? {it.package_unit}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                          {(parseFloat(it.package_qty) > 0 && parseFloat(it.quantity) > 0) && (
                            <span className={`${IPUCU} ${icerikUyarisi(it) ? 'text-[#7a3535] font-semibold' : 'text-[#3d6e4e]'} pr-9`}
                              title="Stoğa girecek toplam miktar">
                              ↳ {((parseFloat(it.quantity) || 0) * parseFloat(it.package_qty)).toLocaleString('tr-TR')}{' '}
                              {satirKarti(it)?.unit || it.package_unit || ''}
                            </span>
                          )}
                        </td>
                        {/* Ambalaj Birim Fiyatı — FİYAT grubunun başı */}
                        <td className={`${HUCRE} ${GRUP} order-9 col-span-2`}>
                          <span className={ETIKET}>Birim Fiyat</span>
                          <Input type="number" step="0.01" min="0" value={it.unit_price || ''} onChange={(e) => updateItem(i, 'unit_price', e.target.value)}
                            placeholder="0,00" className={SAYI} data-testid={`item-price-${i}`} />
                          {(() => {
                            // UYARI, ambalaj içeriği dolu olma şartına BAĞLANMAZ.
                            // Eskiden bütün blok `package_qty > 0` kapısının
                            // arkasındaydı; `icerikUyarisi` içerik boşken
                            // ürettiği 'bos' uyarısını o yüzden hiç
                            // gösteremiyordu (ölçüldü: 5 uyarı görünmüyordu).
                            // Daha kötüsü kullanıcı uyarıyı düzeltmek için
                            // alanı temizlediğinde uyarı da siliniyor, "sorun
                            // çözüldü" izlenimi veriyordu.
                            const sapma = icerikUyarisi(it);
                            const icerikVar = parseFloat(it.package_qty) > 0;
                            return (
                              <>
                                {icerikVar && (
                                  <span className={`${IPUCU} ${sapma ? 'text-[#7a3535] font-semibold' : 'text-[#3d6e4e]'}`}
                                    title="Ürün birim fiyatı">
                                    ↳ {fmtTR((parseFloat(it.unit_price) || 0) / parseFloat(it.package_qty))}{' '}
                                    /{satirKarti(it)?.unit || it.package_unit || '?'}
                                  </span>
                                )}
                                {sapma && sapma.tur === 'sapma' && (
                                  <span className={`${IPUCU} text-[#7a3535] font-semibold`}
                                    title={sapma.kaynak === 'gecmis'
                                      ? 'Bu ürünün diğer faturalarındaki fiyatların medyanı (bu fatura hariç)'
                                      : 'Ürün kartında kayıtlı son alış fiyatı'}>
                                    ⚠ {sapma.kaynak === 'gecmis' ? 'diğer alışlarda' : 'kartta'}{' '}
                                    {fmtTR(sapma.kayitli)} /{sapma.birim} —{' '}
                                    {Math.round(sapma.kat)} kat {sapma.dusuk ? 'düşük' : 'yüksek'}
                                    {sapma.dusuk && '. Ambalaj içeriğini gram yerine kg girmiş olabilirsiniz.'}
                                  </span>
                                )}
                                {sapma && sapma.tur === 'bos' && (
                                  <span className={`${IPUCU} text-[#7a3535] font-semibold`}
                                    title={`Kartta kayıtlı son alış fiyatı: ${fmtTR(sapma.kayitli)} /${sapma.birim}`}>
                                    ⚠ ambalaj içeriği boş — {sapma.kaynak === 'gecmis' ? 'diğer alışlarda' : 'kartta'}{' '}
                                    {fmtTR(sapma.kayitli)} /{sapma.birim},
                                    bu satır {Math.round(sapma.kat)} kat {sapma.dusuk ? 'düşük' : 'yüksek'}.
                                    {!sapma.dusuk && ' Koli/paket alıyorsanız içindeki adedi girin.'}
                                  </span>
                                )}
                                {sapma && sapma.tur === 'buyuk' && (
                                  <span className={`${IPUCU} text-[#7a3535] font-semibold`}
                                    title="Ambalaj içeriği ürünün ana birimi cinsinden yazılır">
                                    ⚠ 1 ambalajda {sapma.icerik.toLocaleString('tr-TR')} {sapma.birim}?
                                    Gram/mililitre girmiş olabilirsiniz.
                                  </span>
                                )}
                              </>
                            );
                          })()}
                          {(parseFloat(it.discount_rate) > 0 && !(parseFloat(it.package_qty) > 0)) && (
                            <span className={`${IPUCU} text-[#3d6e4e]`} title="İskontolu birim fiyat">
                              {fmtTR((parseFloat(it.unit_price) || 0) * (1 - (parseFloat(it.discount_rate) || 0) / 100))}
                            </span>
                          )}
                        </td>
                        {/* İskonto % */}
                        <td className={`${HUCRE} order-10 col-span-2`}>
                          <span className={ETIKET}>İskonto %</span>
                          <Input type="number" step="0.1" min="0" max="100" value={it.discount_rate || ''} onChange={(e) => updateItem(i, 'discount_rate', e.target.value)}
                            placeholder="0" className={SAYI} data-testid={`item-disc-${i}`} />
                        </td>
                        {/* KDV % — kartın oranı VARSAYILAN, değiştirilebilir (kullanıcı,
                            2026-09-28). Farklı seçilirse kartın da değişmesi SORULUR;
                            fark satırın altında yazar, gözden kaçmasın. */}
                        <td className={`${HUCRE} ${GRUP} order-11 col-span-2`}>
                          <span className={ETIKET}>KDV %</span>
                          {(() => {
                            const kartUrun = it.matched_product_id
                              ? products.find(x => x.id === it.matched_product_id) : null;
                            const kartOrani = (kartUrun && kartUrun.kdv_rate != null
                              && kartUrun.kdv_rate !== '') ? String(kartUrun.kdv_rate) : null;
                            const secildi = it.kdv_rate !== '' && it.kdv_rate != null;
                            const deger = secildi ? String(it.kdv_rate) : (kartOrani ?? '');
                            const farkli = kartOrani != null && deger !== ''
                              && parseFloat(deger) !== parseFloat(kartOrani);
                            const firma = it.matched_product_id ? firmaKdv[it.matched_product_id] : null;
                            const firmaOrani = firma?.oran != null ? String(firma.oran) : null;
                            const firmadan = firmaOrani != null && deger !== ''
                              && parseFloat(deger) === parseFloat(firmaOrani);
                            // Kayıtlı faturada listede olmayan bir oran (ör. %2) da görünsün
                            const secenekler = [...new Set(['0', '1', '5', '8', '10', '16', '18', '20',
                              ...(kartOrani != null ? [kartOrani] : []), ...(firmaOrani != null ? [firmaOrani] : []),
                              ...(deger ? [deger] : [])])]
                              .sort((a, b) => parseFloat(a) - parseFloat(b));
                            return (
                              <div>
                                <select value={deger}
                                  onChange={(e) => kdvSec(i, e.target.value, kartUrun, kartOrani)}
                                  title={[
                                    firma ? `Bu firmadan son alım: %${firmaOrani} (${firma.fatura_no || '—'}, ${firma.tarih || ''})` : null,
                                    kartOrani != null ? `Ürün kartındaki oran %${kartOrani} (${kartUrun.code}). Bu faturada farklı oran seçebilirsiniz.` : null,
                                  ].filter(Boolean).join(' · ') || (kartOrani != null
                                    ? `Ürün kartındaki oran %${kartOrani} (${kartUrun.code}). Bu faturada farklı oran seçebilirsiniz.`
                                    : it.matched_product_id
                                      ? 'Bu ürünün kartında KDV oranı tanımlı değil — seçtiğiniz oran ürün kartına da işlenir'
                                      : 'Ürün seçilince kartındaki oran gelir')}
                                  className={`${SECIM} font-mono tabular-nums ${deger === '' ? 'border-[#e0b45a] text-[#8a7e70]' : farkli ? 'border-[#e0b45a]' : ''}`}
                                  data-testid={`item-kdv-${i}`}>
                                  <option value="">— seçin —</option>
                                  {secenekler.map(r => (
                                    <option key={r} value={r}>
                                      %{r}{[r === kartOrani ? 'kart' : null, r === firmaOrani ? 'firma son' : null]
                                        .filter(Boolean).map(x => ` (${x})`).join('')}
                                    </option>
                                  ))}
                                </select>
                                {farkli && (
                                  <span className={`block mt-0.5 text-[9px] leading-tight ${it.kdv_karta_yaz ? 'text-[#2e6fa0]' : 'text-[#8a5c00]'}`}
                                    data-testid={`item-kdv-fark-${i}`}>
                                    {it.kdv_karta_yaz ? `kart da %${deger} olacak`
                                      : firmadan ? `firmadan son alım · kartta %${kartOrani}` : `kartta %${kartOrani}`}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        {/* Depo — yalnızca stoklu/demirbaş kalemlerde anlamlı.
                            Gider seçilince mobilde hücre tamamen kalkar,
                            tür kutusu satırı kaplar. */}
                        <td className={`${HUCRE} order-4 col-span-3 ${itemType === 'expense' ? 'hidden md:table-cell' : ''}`}>
                          <span className={ETIKET}>Depo</span>
                          {itemType === 'expense' ? (
                            <div className="h-8 flex items-center justify-center text-xs text-[#c0b8ad]">—</div>
                          ) : (
                            <select value={it.warehouse_id || ''} onChange={(e) => updateItem(i, 'warehouse_id', e.target.value)}
                              title={it.warehouse_id ? undefined : 'Depo seçilmezse stok girişi yapılmaz'}
                              className={`${SECIM} text-xs ${it.warehouse_id ? '' : 'border-[#e0b45a] text-[#8a7e70]'}`}
                              data-testid={`item-warehouse-${i}`}>
                              <option value="">— Stok girişi yok —</option>
                              {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                            </select>
                          )}
                        </td>
                        {/* Tutar — satırın sonucu; grubun başında ayraç var */}
                        <td className={`${HUCRE} ${GRUP} text-right hidden md:table-cell`}>
                          <span className="font-mono tabular-nums text-[13px] font-semibold text-[#1a1916] leading-8">{fmtTR(lineTotal)}</span>
                          {lineKdv > 0 && (
                            <span className={`${IPUCU} text-[#8a7e70]`}>
                              {form.kdv_inclusive ? `net: ${fmtTR(lineNet)}` : `+${fmtTR(lineKdv)} KDV`}
                            </span>
                          )}
                        </td>
                        <td className="block md:table-cell order-12 col-span-6 px-1 py-1.5 align-top">
                          <Button variant="ghost" onClick={() => removeItem(i)}
                            aria-label="Kalemi sil"
                            className="w-full md:w-8 h-10 md:h-8 justify-center text-[#8a7e70] hover:text-[#7a3535] hover:bg-[#7a3535]/10"
                            data-testid={`item-remove-${i}`}>
                            <X size={14} /><span className="md:hidden ml-1 text-xs">Kalemi sil</span>
                          </Button>
                        </td>
                      </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr><td colSpan={11} className="text-center py-8 text-[#8a7e70] text-sm">Henüz kalem yok — "Kalem Ekle" ile başlayın</td></tr>
                )}
              </tbody>
            </table>
            {items.length > 0 && (
              <div className="border-t border-[#d0c4b0] bg-[#f5f0e8]">
                <div className="flex justify-end p-4">
                  <div className="space-y-1.5 min-w-[260px]">
                    <div className="flex justify-between text-sm text-[#8a7e70]">
                      <span>İskontosuz Toplam</span>
                      <span className="font-mono">{fmtTR(grossTotal)} {form.currency}</span>
                    </div>
                    {discountTotal > 0 && (
                      <div className="flex justify-between text-sm text-[#7a3535]">
                        <span>İskonto</span>
                        <span className="font-mono">-{fmtTR(discountTotal)} {form.currency}</span>
                      </div>
                    )}
                    {discountTotal > 0 && (
                      <div className="flex justify-between text-sm text-[#8a7e70] border-t border-[#d0c4b0] pt-1.5">
                        <span>Net Toplam</span>
                        <span className="font-mono">{fmtTR(grossTotal - discountTotal)} {form.currency}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm text-[#8a7e70]">
                      <span>KDV Matrahı</span>
                      <span className="font-mono">{fmtTR(itemsSubtotal)} {form.currency}</span>
                    </div>
                    <div className="flex justify-between text-sm text-[#8a7e70]">
                      <span>KDV Tutarı</span>
                      <span className="font-mono">{fmtTR(itemsKdv)} {form.currency}</span>
                    </div>
                    <div className="flex justify-between text-base font-bold text-[#1a1916] border-t border-[#d0c4b0] pt-1.5 mt-1.5">
                      <span>Genel Toplam</span>
                      <span className="font-mono">{fmtTR(genelToplam)} {form.currency}</span>
                    </div>
                    {form.currency !== 'TRY' && parseFloat(form.exchange_rate) > 0 && (
                      <div className="flex justify-between text-xs text-[#8a7e70]">
                        <span>TL Karşılığı</span>
                        <span className="font-mono">{fmtTR(genelToplam * parseFloat(form.exchange_rate || 1))} TL</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      {/* Mobilde yapiskan alt cubuk: toplam + kaydet her zaman erisilebilir */}
      <div className="md:hidden sticky bottom-0 -mx-4 mt-3 px-4 py-2 bg-[#faf7f2]
                      border-t border-[#d0c4b0] flex items-center gap-3 z-30"
           data-testid="mobil-kaydet-cubugu">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] text-[#8a7e70] uppercase leading-none">Genel Toplam</p>
          <p className="font-mono tabular-nums text-base font-bold text-[#1a1916] truncate">
            {fmtTR(genelToplam)} {form.currency}
          </p>
        </div>
        <Button onClick={handleSubmit} disabled={loading}
          className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold h-11 px-5 shrink-0"
          data-testid="save-invoice-btn-mobil">
          <Save size={15} className="mr-1.5" />
          {loading ? 'Kaydediliyor...' : 'Kaydet'}
        </Button>
      </div>

      </div>

      {/* Dosya kutusu seçimi */}
      {kutuAcik && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setKutuAcik(false)}>
          <div className="bg-[#faf7f2] rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
            onClick={e => e.stopPropagation()} data-testid="kutu-secim-penceresi">
            <div className="flex items-center justify-between p-4 border-b border-[#d0c4b0]">
              <h2 className="text-base font-bold text-[#1a1916] flex items-center gap-2">
                <Inbox size={17} /> Dosya Kutusu
              </h2>
              <button onClick={() => setKutuAcik(false)} className="text-[#8a7e70] hover:text-[#1a1916]">
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto p-3 space-y-2">
              {kutuBelgeleri.length === 0 ? (
                <p className="text-center py-10 text-[#8a7e70]">
                  Kutuda işlenecek belge yok
                </p>
              ) : kutuBelgeleri.map(b => (
                <button key={b.id} type="button" onClick={() => kutudanSec(b)}
                  className="w-full text-left bg-white border border-[#d0c4b0] rounded-lg p-3
                             hover:border-[#3d6e4e] transition-colors"
                  data-testid={`kutu-belge-${b.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-[#1a1916]">{b.tur_adi}</span>
                    <span className="text-xs text-[#8a7e70]">{b.sayfa_sayisi} sayfa</span>
                  </div>
                  <p className="text-xs text-[#8a7e70] mt-0.5">
                    {b.yukleyen_adi} · {new Date(b.yuklendi_at).toLocaleString('tr-TR',
                      { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {b.notlar && <p className="text-xs text-[#3F3E39] italic mt-0.5">{b.notlar}</p>}
                  {b.red_notu && (
                    <p className="text-xs text-[#7a3535] mt-0.5">
                      Düzeltme istendi: {b.red_notu.gerekce}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowUploadModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-[#1a1916]">Fatura Yükle</h2>
              <button onClick={() => setShowUploadModal(false)} className="text-[#8a7e70] hover:text-[#1a1916]"><X size={18} /></button>
            </div>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => {
                e.preventDefault();
                setDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file) handleFileParse(null, file);
              }}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-10 flex flex-col items-center gap-3 cursor-pointer transition-colors
                ${dragOver ? 'border-[#3d6e4e] bg-[#3d6e4e]/5' : 'border-[#d0c4b0] hover:border-[#8a7e70] bg-[#faf7f2]'}`}
            >
              <Upload size={32} className={dragOver ? 'text-[#3d6e4e]' : 'text-[#8a7e70]'} />
              <p className="text-sm font-semibold text-[#1a1916]">Dosyayı buraya sürükle</p>
              <p className="text-xs text-[#8a7e70]">veya tıklayarak seç</p>
              <p className="text-[10px] text-[#b0a090]">PNG, JPG, PDF desteklenir</p>
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,application/pdf,.pdf" onChange={handleFileParse} className="hidden" />
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && imagePreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={() => setShowImageModal(false)}>
          <div className="relative max-w-4xl max-h-[90vh] mx-4" onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowImageModal(false)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 font-semibold text-sm">
              <X size={24} />
            </button>
            <img src={imagePreview} alt="Fatura önizleme"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" />
          </div>
        </div>
      )}

      {/* ── Fatura Silme: iki aşamalı onay ── */}
      {kdvSoru && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setKdvSoru(null)} data-testid="kdv-soru">
          <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#e8dfd0]">
              <h3 className="text-sm font-bold text-[#1a1916]">KDV oranı ürün kartından farklı</h3>
              <p className="text-xs text-[#8a7e70] mt-1">{kdvSoru.kod} {kdvSoru.urun}</p>
            </div>
            <div className="px-5 py-4 text-sm text-[#3F3E39] space-y-2">
              <p>
                Ürün kartında <b>%{kdvSoru.kartOrani}</b> tanımlı, bu faturada <b>%{kdvSoru.yeni}</b> seçtiniz.
                Ürün kartındaki oran da <b>%{kdvSoru.yeni}</b> olsun mu?
              </p>
              <p className="text-xs text-[#8a7e70]">
                Kaydedilmiş faturalar etkilenmez — her fatura kendi oranını saklar. Kart yalnızca
                bundan sonraki faturalarda gelen varsayılan oranı belirler; değişiklik bu fatura
                kaydedilince uygulanır.
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-5 py-4 border-t border-[#e8dfd0]">
              <Button variant="outline" className="border-[#d0c4b0] h-9 text-xs"
                onClick={() => setKdvSoru(null)} data-testid="kdv-soru-yalniz-fatura">
                Yalnızca bu faturada %{kdvSoru.yeni}
              </Button>
              <Button className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] h-9 text-xs"
                onClick={() => {
                  const { i } = kdvSoru;
                  setItems(onceki => onceki.map((x, j) => (j === i ? { ...x, kdv_karta_yaz: true } : x)));
                  setKdvSoru(null);
                }} data-testid="kdv-soru-karti-guncelle">
                Ürün kartını da %{kdvSoru.yeni} yap
              </Button>
            </div>
          </div>
        </div>
      )}

      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !deleting && setShowDelete(false)} role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            {/* Başlık */}
            <div className="flex items-start gap-3 p-5 border-b border-[#e8dfd0]">
              <div className="w-10 h-10 rounded-full bg-[#7a3535]/10 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} className="text-[#7a3535]" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-[#1a1916]">
                  {deleteStep === 1 ? 'Faturayı silmek üzeresiniz' : 'Son onay'}
                </h2>
                <p className="text-xs text-[#8a7e70] mt-0.5">
                  {deleteStep === 1
                    ? 'Aşağıdaki kayıtlar da birlikte geri alınacak.'
                    : 'Bu işlem geri alınamaz.'}
                </p>
              </div>
            </div>

            {/* İçerik */}
            <div className="p-5">
              {!deletePreview ? (
                <p className="text-sm text-[#8a7e70]">Yükleniyor…</p>
              ) : deleteStep === 1 ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-[#d0c4b0] bg-[#faf7f2] p-3 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-[#8a7e70]">Fatura No</span>
                      <span className="font-semibold text-[#1a1916]">{deletePreview.invoice_number || '—'}</span>
                    </div>
                    <div className="flex justify-between gap-3 mt-1">
                      <span className="text-[#8a7e70]">Tarih</span>
                      <span className="text-[#1a1916]">{deletePreview.invoice_date || '—'}</span>
                    </div>
                    {deletePreview.supplier_name && (
                      <div className="flex justify-between gap-3 mt-1">
                        <span className="text-[#8a7e70]">Tedarikçi</span>
                        <span className="text-[#1a1916] truncate max-w-[60%]">{deletePreview.supplier_name}</span>
                      </div>
                    )}
                    <div className="flex justify-between gap-3 mt-1">
                      <span className="text-[#8a7e70]">Tutar</span>
                      <span className="font-mono font-semibold text-[#1a1916]">
                        {fmtTR(deletePreview.amount_foreign || 0)} {deletePreview.currency}
                      </span>
                    </div>
                  </div>

                  <ul className="space-y-1.5 text-sm">
                    <li className="flex items-start gap-2">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${deletePreview.ledger_count ? 'bg-[#7a3535]' : 'bg-[#d0c4b0]'}`} />
                      <span className={deletePreview.ledger_count ? 'text-[#1a1916]' : 'text-[#8a7e70]'}>
                        {deletePreview.ledger_count
                          ? <>Cari hesaptan <strong>{fmtTR(deletePreview.ledger_total)} {deletePreview.currency}</strong> tutarındaki borç kaydı silinecek</>
                          : 'Cari hesapta bağlı borç kaydı yok'}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${deletePreview.movement_count ? 'bg-[#7a3535]' : 'bg-[#d0c4b0]'}`} />
                      <span className={deletePreview.movement_count ? 'text-[#1a1916]' : 'text-[#8a7e70]'}>
                        {deletePreview.movement_count
                          ? <><strong>{deletePreview.movement_count}</strong> stok hareketi silinecek ve stok miktarları geri alınacak</>
                          : 'Bağlı stok hareketi yok'}
                      </span>
                    </li>
                  </ul>

                  {deletePreview.stock_effects?.length > 0 && (
                    <div className="rounded-lg border border-[#d0c4b0] overflow-hidden">
                      <div className="bg-[#e8dfd0] px-3 py-1.5 text-[11px] font-bold uppercase text-[#1a1916]">
                        Stoktan düşülecek
                      </div>
                      <div className="max-h-40 overflow-y-auto divide-y divide-[#e8dfd0]">
                        {deletePreview.stock_effects.map((s, i) => (
                          <div key={i} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
                            <span className="text-[#1a1916] truncate">{s.product_name}</span>
                            <span className="text-[#8a7e70] shrink-0">{s.warehouse_name}</span>
                            <span className="font-mono tabular-nums text-[#7a3535] shrink-0">
                              −{Number(s.quantity || 0).toLocaleString('tr-TR')} {s.unit}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-[#e0b4b4] bg-[#7a3535]/8 p-4 text-sm text-[#7a3535]">
                  <p className="font-semibold">
                    “{deletePreview.invoice_number || 'Bu fatura'}” numaralı fatura kalıcı olarak silinecek.
                  </p>
                  <p className="mt-1.5 text-xs">
                    Cari borç kaydı ve stok hareketleri de geri alınacak. Bu işlemin geri dönüşü yoktur.
                  </p>
                </div>
              )}
            </div>

            {/* Aksiyonlar */}
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-[#e8dfd0] bg-[#faf7f2] rounded-b-xl">
              <Button variant="outline" onClick={() => setShowDelete(false)} disabled={deleting}
                className="border-[#d0c4b0] h-9 text-xs">
                Vazgeç
              </Button>
              {deleteStep === 1 ? (
                <Button onClick={() => setDeleteStep(2)} disabled={!deletePreview}
                  className="bg-[#7a3535] text-white hover:bg-[#5e2929] h-9 text-xs font-semibold"
                  data-testid="delete-step1-btn">
                  Devam Et
                </Button>
              ) : (
                <Button onClick={confirmDelete} disabled={deleting}
                  className="bg-[#7a3535] text-white hover:bg-[#5e2929] h-9 text-xs font-semibold"
                  data-testid="delete-confirm-btn">
                  <Trash2 size={13} className="mr-1.5" />
                  {deleting ? 'Siliniyor…' : 'Evet, kalıcı olarak sil'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
